// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as Core from './configuration.core';
import * as ModelGrid from './configuration.modelGrid';

export enum FieldValidationStates {
    Valid,
    SoftInvalid,
    HardInvalid
}

export enum ValidationMessageStates {
    Info,
    SoftInvalid,
    HardInvalid
}

export enum FieldTypes {
    Unassigned,
    String,
    Numeric,
    Boolean,
    StringEnumerable,
    NumericEnumerable,
    MultiString,
    MultiNumeric
}

export function isGridModel(source: any): source is IGrid {
    // Method Implementation: simple
    return source.modelName === 'Grid';
}

export function isHistoricalStateModel(source: any): source is IHistoricalState {
    // Method Implementation: simple
    return source.modelName === 'HistoricalState';
}

export function isColumnModel(source: any): source is IColumn {
    // Method Implementation: simple
    return source.modelName === 'Column';
}

export function isColumnAnswerModel(source: any): source is IColumnAnswer {
    // Method Implementation: simple
    return source.modelName === 'ColumnAnswer';
}

export function isRowModel(source: any): source is IRow {
    // Method Implementation: simple
    return source.modelName === 'Row';
}

export function isFieldModel(source: any): source is IField {
    // Method Implementation: simple
    return source.modelName === 'Field';
}

export function isStringFieldModel(source: any): source is IStringField {
    // Method Implementation: simple
    return source.modelName === 'StringField';
}

export function isNumericFieldModel(source: any): source is INumericField {
    // Method Implementation: simple
    return source.modelName === 'NumericField';
}

export function isBooleanFieldModel(source: any): source is IBooleanField {
    // Method Implementation: simple
    return source.modelName === 'BooleanField';
}

export function isStringEnumerableFieldModel(source: any): source is IStringEnumerableField {
    // Method Implementation: simple
    return source.modelName === 'StringEnumerableField';
}

export function isNumericEnumerableFieldModel(source: any): source is INumericEnumerableField {
    // Method Implementation: simple
    return source.modelName === 'NumericEnumerableField';
}

export function isMultiStringFieldModel(source: any): source is IMultiStringField {
    // Method Implementation: simple
    return source.modelName === 'MultiStringField';
}

export function isMultiNumericFieldModel(source: any): source is IMultiNumericField {
    // Method Implementation: simple
    return source.modelName === 'MultiNumericField';
}

export function isChildGridFieldModel(source: any): source is IChildGridField {
    // Method Implementation: simple
    return source.modelName === 'ChildGridField';
}

export function isAnswerModel(source: any): source is IAnswer {
    // Method Implementation: simple
    return source.modelName === 'Answer';
}

export function isStringAnswerModel(source: any): source is IStringAnswer {
    // Method Implementation: simple
    return source.modelName === 'StringAnswer';
}

export function isNumericAnswerModel(source: any): source is INumericAnswer {
    // Method Implementation: simple
    return source.modelName === 'NumericAnswer';
}

export function isValidationMessageModel(source: any): source is IValidationMessage {
    // Method Implementation: simple
    return source.modelName === 'ValidationMessage';
}

export function isConflictScenarioModel(source: any): source is IConflictScenario {
    // Method Implementation: simple
    return source.modelName === 'ConflictScenario';
}

export function isConflictScenarioChangedFieldModel(source: any): source is IConflictScenarioChangedField {
    // Method Implementation: simple
    return source.modelName === 'ConflictScenarioChangedField';
}

export function isRecommendedValueModel(source: any): source is IRecommendedValue {
    // Method Implementation: simple
    return source.modelName === 'RecommendedValue';
}

export interface GridZodSchemaShape {
    columns?: ColumnZodSchemaShape[];

    rows?: RowZodSchemaShape[];

    readonly?: boolean;

    conflict?: ConflictScenarioZodSchemaShape;

    validationMessages?: ValidationMessageZodSchemaShape[];

    state?: 'Valid' | 'Invalid' | 'Incomplete';

    historicalStates?: HistoricalStateZodSchemaShape[];
}

export interface HistoricalStateZodSchemaShape {
    description?: string;
}

export interface ColumnZodSchemaShape {
    name?: string;

    displayName?: string;

    helpText?: string;

    source?: string;

    fieldType?: 'Unassigned' | 'String' | 'Numeric' | 'Boolean' | 'StringEnumerable' | 'NumericEnumerable' | 'MultiString' | 'MultiNumeric';

    visible?: boolean;

    customizable?: boolean;

    pinned?: 'Unassigned' | 'Left' | 'Right';

    variant?: string;

    group?: string;

    groupDisplay?: 'Unassigned' | 'Open' | 'Closed';

    sortable?: boolean;

    sortDirection?: 'Unassigned' | 'Ascending' | 'Descending';

    sortIndex?: number;

    type?: 'String' | 'Numeric' | 'Boolean';

    answers?: ColumnAnswerZodSchemaShape[];

    fastDataEntry?: string;
}

export interface ColumnAnswerZodSchemaShape {
    name?: string;

    displayName?: string;

    enabled?: boolean;

    visible?: boolean;

    assignmentState?: 'Assignable' | 'Conflicting' | 'Blocked';
}

export interface RowZodSchemaShape {
    entityId?: string;

    validationMessages?: ValidationMessageZodSchemaShape[];

    state?: 'Valid' | 'Invalid' | 'Incomplete';

    fields?: FieldZodSchemaShape[];
}

export interface FieldZodSchemaShape {
    fieldType?: string;

    columnName?: string;

    source?: string;

    enabled?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;
}

export interface StringFieldZodSchemaShape {
    fieldType?: string;

    columnName?: string;

    source?: string;

    enabled?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    value?: string;
}

export interface NumericFieldZodSchemaShape {
    fieldType?: string;

    columnName?: string;

    source?: string;

    enabled?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    value?: number;

    precision?: number;

    min?: number;

    max?: number;
}

export interface BooleanFieldZodSchemaShape {
    fieldType?: string;

    columnName?: string;

    source?: string;

    enabled?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    value?: boolean;
}

export interface StringEnumerableFieldZodSchemaShape {
    fieldType?: string;

    columnName?: string;

    source?: string;

    enabled?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    value?: string;

    allowCustomValue?: boolean;

    answers?: StringAnswerZodSchemaShape[];
}

export interface NumericEnumerableFieldZodSchemaShape {
    fieldType?: string;

    columnName?: string;

    source?: string;

    enabled?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    value?: number;

    allowCustomValue?: boolean;

    answers?: NumericAnswerZodSchemaShape[];

    precision?: number;

    min?: number;

    max?: number;
}

export interface MultiStringFieldZodSchemaShape {
    fieldType?: string;

    columnName?: string;

    source?: string;

    enabled?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    values?: string[];
}

export interface MultiNumericFieldZodSchemaShape {
    fieldType?: string;

    columnName?: string;

    source?: string;

    enabled?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    values?: number[];

    precision?: number;

    min?: number;

    max?: number;
}

export interface ChildGridFieldZodSchemaShape {
    fieldType?: string;

    columnName?: string;

    source?: string;

    enabled?: boolean;

    required?: boolean;

    validationState?: 'Valid' | 'SoftInvalid' | 'HardInvalid';

    validationMessages?: ValidationMessageZodSchemaShape[];

    displayValue?: string;

    assignmentState?: 'Unassigned' | 'UserAssigned' | 'SystemAssigned';

    recommendedDisplayValue?: string;

    grid?: GridZodSchemaShape;
}

export interface AnswerZodSchemaShape {
    answerType?: string;

    name?: string;

    displayName?: string;

    enabled?: boolean;

    visible?: boolean;

    assignmentState?: 'Assignable' | 'Conflicting' | 'Blocked';
}

export interface StringAnswerZodSchemaShape {
    answerType?: string;

    name?: string;

    displayName?: string;

    enabled?: boolean;

    visible?: boolean;

    assignmentState?: 'Assignable' | 'Conflicting' | 'Blocked';

    value?: string;
}

export interface NumericAnswerZodSchemaShape {
    answerType?: string;

    name?: string;

    displayName?: string;

    enabled?: boolean;

    visible?: boolean;

    assignmentState?: 'Assignable' | 'Conflicting' | 'Blocked';

    value?: number;
}

export interface ValidationMessageZodSchemaShape {
    message?: string;

    type?: 'Info' | 'SoftInvalid' | 'HardInvalid';

    transient?: boolean;
}

export interface ConflictScenarioZodSchemaShape {
    fields?: ConflictScenarioChangedFieldZodSchemaShape[];
}

export interface ConflictScenarioChangedFieldZodSchemaShape {
    source?: string;

    columnName?: string;

    oldValue?: string;

    newValue?: string;
}

export interface RecommendedValueZodSchemaShape {
    source?: string;

    displayValue?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const GridZodSchema: Zod.ZodSchema<GridZodSchemaShape> = Zod.lazy(() => Zod.object({
    columns: Zod.array(ColumnZodSchema).optional(),
    rows: Zod.array(RowZodSchema).optional(),
    readonly: Zod.boolean().optional(),
    conflict: ConflictScenarioZodSchema.optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    state: Zod.enum([ 'Valid', 'Invalid', 'Incomplete' ]).optional(),
    historicalStates: Zod.array(HistoricalStateZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const HistoricalStateZodSchema: Zod.ZodSchema<HistoricalStateZodSchemaShape> = Zod.lazy(() => Zod.object({
    description: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ColumnZodSchema: Zod.ZodSchema<ColumnZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    displayName: Zod.string().optional(),
    helpText: Zod.string().optional(),
    source: Zod.string().optional(),
    fieldType: Zod.enum([ 'Unassigned', 'String', 'Numeric', 'Boolean', 'StringEnumerable', 'NumericEnumerable', 'MultiString', 'MultiNumeric' ]).optional(),
    visible: Zod.boolean().optional(),
    customizable: Zod.boolean().optional(),
    pinned: Zod.enum([ 'Unassigned', 'Left', 'Right' ]).optional(),
    variant: Zod.string().optional(),
    group: Zod.string().optional(),
    groupDisplay: Zod.enum([ 'Unassigned', 'Open', 'Closed' ]).optional(),
    sortable: Zod.boolean().optional(),
    sortDirection: Zod.enum([ 'Unassigned', 'Ascending', 'Descending' ]).optional(),
    sortIndex: Zod.number().optional(),
    type: Zod.enum([ 'String', 'Numeric', 'Boolean' ]).optional(),
    answers: Zod.array(ColumnAnswerZodSchema).optional(),
    fastDataEntry: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ColumnAnswerZodSchema: Zod.ZodSchema<ColumnAnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    displayName: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    assignmentState: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const RowZodSchema: Zod.ZodSchema<RowZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityId: Zod.string().optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    state: Zod.enum([ 'Valid', 'Invalid', 'Incomplete' ]).optional(),
    fields: Zod.array(FieldZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const FieldZodSchema: Zod.ZodSchema<FieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    columnName: Zod.string().optional(),
    source: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringFieldZodSchema: Zod.ZodSchema<StringFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    columnName: Zod.string().optional(),
    source: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericFieldZodSchema: Zod.ZodSchema<NumericFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    columnName: Zod.string().optional(),
    source: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    value: Zod.number().optional(),
    precision: Zod.number().optional(),
    min: Zod.number().optional(),
    max: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const BooleanFieldZodSchema: Zod.ZodSchema<BooleanFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    columnName: Zod.string().optional(),
    source: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    value: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringEnumerableFieldZodSchema: Zod.ZodSchema<StringEnumerableFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    columnName: Zod.string().optional(),
    source: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    value: Zod.string().optional(),
    allowCustomValue: Zod.boolean().optional(),
    answers: Zod.array(StringAnswerZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericEnumerableFieldZodSchema: Zod.ZodSchema<NumericEnumerableFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    columnName: Zod.string().optional(),
    source: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    value: Zod.number().optional(),
    allowCustomValue: Zod.boolean().optional(),
    answers: Zod.array(NumericAnswerZodSchema).optional(),
    precision: Zod.number().optional(),
    min: Zod.number().optional(),
    max: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MultiStringFieldZodSchema: Zod.ZodSchema<MultiStringFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    columnName: Zod.string().optional(),
    source: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    values: Zod.array(Zod.string()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MultiNumericFieldZodSchema: Zod.ZodSchema<MultiNumericFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    columnName: Zod.string().optional(),
    source: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    values: Zod.array(Zod.number()).optional(),
    precision: Zod.number().optional(),
    min: Zod.number().optional(),
    max: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ChildGridFieldZodSchema: Zod.ZodSchema<ChildGridFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    columnName: Zod.string().optional(),
    source: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    validationState: Zod.enum([ 'Valid', 'SoftInvalid', 'HardInvalid' ]).optional(),
    validationMessages: Zod.array(ValidationMessageZodSchema).optional(),
    displayValue: Zod.string().optional(),
    assignmentState: Zod.enum([ 'Unassigned', 'UserAssigned', 'SystemAssigned' ]).optional(),
    recommendedDisplayValue: Zod.string().optional(),
    grid: GridZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const AnswerZodSchema: Zod.ZodSchema<AnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional(),
    displayName: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    assignmentState: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringAnswerZodSchema: Zod.ZodSchema<StringAnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional(),
    displayName: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    assignmentState: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericAnswerZodSchema: Zod.ZodSchema<NumericAnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    answerType: Zod.string(),
    name: Zod.string().optional(),
    displayName: Zod.string().optional(),
    enabled: Zod.boolean().optional(),
    visible: Zod.boolean().optional(),
    assignmentState: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional(),
    value: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ValidationMessageZodSchema: Zod.ZodSchema<ValidationMessageZodSchemaShape> = Zod.lazy(() => Zod.object({
    message: Zod.string().optional(),
    type: Zod.enum([ 'Info', 'SoftInvalid', 'HardInvalid' ]).optional(),
    transient: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConflictScenarioZodSchema: Zod.ZodSchema<ConflictScenarioZodSchemaShape> = Zod.lazy(() => Zod.object({
    fields: Zod.array(ConflictScenarioChangedFieldZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConflictScenarioChangedFieldZodSchema: Zod.ZodSchema<ConflictScenarioChangedFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    source: Zod.string().optional(),
    columnName: Zod.string().optional(),
    oldValue: Zod.string().optional(),
    newValue: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const RecommendedValueZodSchema: Zod.ZodSchema<RecommendedValueZodSchemaShape> = Zod.lazy(() => Zod.object({
    source: Zod.string().optional(),
    displayValue: Zod.string().optional()
}).nonstrict());

export interface IGrid extends Wellspring.IModel {
    readonly modelName: string;

    readonly columns: Column[];

    readonly rows: Row[];

    readonly readonly: boolean;

    readonly conflict: ConflictScenario;

    readonly validationMessages: ValidationMessage[];

    readonly state: Core.EntityStates;

    readonly historicalStates: HistoricalState[];

    readonly isColumnsDefined: boolean;

    readonly isRowsDefined: boolean;

    readonly isReadonlyDefined: boolean;

    readonly isConflictDefined: boolean;

    readonly isValidationMessagesDefined: boolean;

    readonly isStateDefined: boolean;

    readonly isHistoricalStatesDefined: boolean;
}

export interface IHistoricalState extends Wellspring.IModel {
    readonly modelName: string;

    readonly description: string;

    readonly isDescriptionDefined: boolean;
}

export interface IColumn extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly displayName: string;

    readonly helpText: string;

    readonly source: string;

    readonly fieldType: FieldTypes;

    readonly visible: boolean;

    readonly customizable: boolean;

    readonly pinned: ModelGrid.PinningLocations;

    readonly variant: string;

    readonly group: string;

    readonly groupDisplay: ModelGrid.GroupDisplays;

    readonly sortable: boolean;

    readonly sortDirection: ModelGrid.SortingDirections;

    readonly sortIndex: number;

    readonly type: ModelGrid.ColumnTypes;

    readonly answers: ColumnAnswer[];

    readonly fastDataEntry: string;

    readonly isNameDefined: boolean;

    readonly isDisplayNameDefined: boolean;

    readonly isHelpTextDefined: boolean;

    readonly isSourceDefined: boolean;

    readonly isFieldTypeDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isCustomizableDefined: boolean;

    readonly isPinnedDefined: boolean;

    readonly isVariantDefined: boolean;

    readonly isGroupDefined: boolean;

    readonly isGroupDisplayDefined: boolean;

    readonly isSortableDefined: boolean;

    readonly isSortDirectionDefined: boolean;

    readonly isSortIndexDefined: boolean;

    readonly isTypeDefined: boolean;

    readonly isAnswersDefined: boolean;

    readonly isFastDataEntryDefined: boolean;
}

export interface IColumnAnswer extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly displayName: string;

    readonly enabled: boolean;

    readonly visible: boolean;

    readonly assignmentState: Core.OptionStates;

    readonly isNameDefined: boolean;

    readonly isDisplayNameDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isAssignmentStateDefined: boolean;
}

export interface IRow extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityId: string;

    readonly validationMessages: ValidationMessage[];

    readonly state: Core.EntityStates;

    readonly fields: AField[];

    readonly isEntityIdDefined: boolean;

    readonly isValidationMessagesDefined: boolean;

    readonly isStateDefined: boolean;

    readonly isFieldsDefined: boolean;
}

export interface IField extends Wellspring.IModel {
    readonly modelName: string;

    readonly columnName: string;

    readonly source: string;

    readonly enabled: boolean;

    readonly required: boolean;

    readonly validationState: FieldValidationStates;

    readonly validationMessages: ValidationMessage[];

    readonly displayValue: string;

    readonly assignmentState: Core.AssignmentStates;

    readonly recommendedDisplayValue: string;

    readonly isColumnNameDefined: boolean;

    readonly isSourceDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isRequiredDefined: boolean;

    readonly isValidationStateDefined: boolean;

    readonly isValidationMessagesDefined: boolean;

    readonly isDisplayValueDefined: boolean;

    readonly isAssignmentStateDefined: boolean;

    readonly isRecommendedDisplayValueDefined: boolean;
}

export interface IStringField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value?: string;

    readonly isValueDefined: boolean;
}

export interface INumericField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value?: number;

    readonly precision: number;

    readonly min: number;

    readonly max: number;

    readonly isValueDefined: boolean;

    readonly isPrecisionDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;
}

export interface IBooleanField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value?: boolean;

    readonly isValueDefined: boolean;
}

export interface IStringEnumerableField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value?: string;

    readonly allowCustomValue: boolean;

    readonly answers: StringAnswer[];

    readonly isValueDefined: boolean;

    readonly isAllowCustomValueDefined: boolean;

    readonly isAnswersDefined: boolean;
}

export interface INumericEnumerableField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value?: number;

    readonly allowCustomValue: boolean;

    readonly answers: NumericAnswer[];

    readonly precision: number;

    readonly min: number;

    readonly max: number;

    readonly isValueDefined: boolean;

    readonly isAllowCustomValueDefined: boolean;

    readonly isAnswersDefined: boolean;

    readonly isPrecisionDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;
}

export interface IMultiStringField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly values?: string[];

    readonly isValuesDefined: boolean;
}

export interface IMultiNumericField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly values?: number[];

    readonly precision: number;

    readonly min: number;

    readonly max: number;

    readonly isValuesDefined: boolean;

    readonly isPrecisionDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;
}

export interface IChildGridField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly grid: Grid;

    readonly isGridDefined: boolean;
}

export interface IAnswer extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly displayName: string;

    readonly enabled: boolean;

    readonly visible: boolean;

    readonly assignmentState: Core.OptionStates;

    readonly isNameDefined: boolean;

    readonly isDisplayNameDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isAssignmentStateDefined: boolean;
}

export interface IStringAnswer extends IAnswer, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericAnswer extends IAnswer, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export interface IValidationMessage extends Wellspring.IModel {
    readonly modelName: string;

    readonly message: string;

    readonly type: ValidationMessageStates;

    readonly transient: boolean;

    readonly isMessageDefined: boolean;

    readonly isTypeDefined: boolean;

    readonly isTransientDefined: boolean;
}

export interface IConflictScenario extends Wellspring.IModel {
    readonly modelName: string;

    readonly fields: ConflictScenarioChangedField[];

    readonly isFieldsDefined: boolean;
}

export interface IConflictScenarioChangedField extends Wellspring.IModel {
    readonly modelName: string;

    readonly source: string;

    readonly columnName: string;

    readonly oldValue: string;

    readonly newValue: string;

    readonly isSourceDefined: boolean;

    readonly isColumnNameDefined: boolean;

    readonly isOldValueDefined: boolean;

    readonly isNewValueDefined: boolean;
}

export interface IRecommendedValue extends Wellspring.IModel {
    readonly modelName: string;

    readonly source: string;

    readonly displayValue: string;

    readonly isSourceDefined: boolean;

    readonly isDisplayValueDefined: boolean;
}

export class Grid implements IGrid {
    public static readonly modelName: string = 'Grid';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'columns', 'rows', 'readonly', 'conflict', 'validationMessages', 'state', 'historicalStates' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Grid.modelName;
    }

    private _columns: Column[];

    private _rows: Row[];

    private _readonly: boolean;

    private _conflict: ConflictScenario;

    private _validationMessages: ValidationMessage[];

    private _state: Core.EntityStates;

    private _historicalStates: HistoricalState[];

    public get columns(): Column[] {
        // Method Implementation: simple
        return this._columns;
    }

    public get rows(): Row[] {
        // Method Implementation: simple
        return this._rows;
    }

    public get readonly(): boolean {
        // Method Implementation: simple
        return this._readonly;
    }

    public get conflict(): ConflictScenario {
        // Method Implementation: simple
        return this._conflict;
    }

    public get validationMessages(): ValidationMessage[] {
        // Method Implementation: simple
        return this._validationMessages;
    }

    public get state(): Core.EntityStates {
        // Method Implementation: simple
        return this._state;
    }

    public get historicalStates(): HistoricalState[] {
        // Method Implementation: simple
        return this._historicalStates;
    }

    public set columns(value: Column[]) {
        // Method Implementation: simple
        this._columns = value;
        this._isColumnsDefined = true;
    }

    public set rows(value: Row[]) {
        // Method Implementation: simple
        this._rows = value;
        this._isRowsDefined = true;
    }

    public set readonly(value: boolean) {
        // Method Implementation: simple
        this._readonly = value;
        this._isReadonlyDefined = true;
    }

    public set conflict(value: ConflictScenario) {
        // Method Implementation: simple
        this._conflict = value;
        this._isConflictDefined = true;
    }

    public set validationMessages(value: ValidationMessage[]) {
        // Method Implementation: simple
        this._validationMessages = value;
        this._isValidationMessagesDefined = true;
    }

    public set state(value: Core.EntityStates) {
        // Method Implementation: simple
        this._state = value;
        this._isStateDefined = true;
    }

    public set historicalStates(value: HistoricalState[]) {
        // Method Implementation: simple
        this._historicalStates = value;
        this._isHistoricalStatesDefined = true;
    }

    private _isColumnsDefined: boolean;

    private _isRowsDefined: boolean;

    private _isReadonlyDefined: boolean;

    private _isConflictDefined: boolean;

    private _isValidationMessagesDefined: boolean;

    private _isStateDefined: boolean;

    private _isHistoricalStatesDefined: boolean;

    public get isColumnsDefined(): boolean {
        // Method Implementation: simple
        return this._isColumnsDefined;
    }

    public get isRowsDefined(): boolean {
        // Method Implementation: simple
        return this._isRowsDefined;
    }

    public get isReadonlyDefined(): boolean {
        // Method Implementation: simple
        return this._isReadonlyDefined;
    }

    public get isConflictDefined(): boolean {
        // Method Implementation: simple
        return this._isConflictDefined;
    }

    public get isValidationMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isValidationMessagesDefined;
    }

    public get isStateDefined(): boolean {
        // Method Implementation: simple
        return this._isStateDefined;
    }

    public get isHistoricalStatesDefined(): boolean {
        // Method Implementation: simple
        return this._isHistoricalStatesDefined;
    }

    public constructor();
    public constructor(source: IGrid);
    public constructor(source: Partial<Pick<Grid, 'columns' | 'rows' | 'readonly' | 'conflict' | 'validationMessages' | 'state' | 'historicalStates'>>);
    public constructor(source?: IGrid | Partial<Pick<Grid, 'columns' | 'rows' | 'readonly' | 'conflict' | 'validationMessages' | 'state' | 'historicalStates'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isGridModel(source)) {
                if (source.isColumnsDefined) {
                    this._columns = source.columns;
                    this._isColumnsDefined = true;
                }
                else {
                    this._columns = [];
                    this._isColumnsDefined = true;
                }
                
                if (source.isRowsDefined) {
                    this._rows = source.rows;
                    this._isRowsDefined = true;
                }
                else {
                    this._rows = [];
                    this._isRowsDefined = true;
                }
                
                if (source.isReadonlyDefined) {
                    this._readonly = source.readonly;
                    this._isReadonlyDefined = true;
                }
                else {
                    this._readonly = false;
                    this._isReadonlyDefined = false;
                }
                
                if (source.isConflictDefined) {
                    this._conflict = source.conflict;
                    this._isConflictDefined = true;
                }
                else {
                    this._conflict = new ConflictScenario();
                    this._isConflictDefined = true;
                }
                
                if (source.isValidationMessagesDefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.isStateDefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = Core.EntityStates.Valid;
                    this._isStateDefined = false;
                }
                
                if (source.isHistoricalStatesDefined) {
                    this._historicalStates = source.historicalStates;
                    this._isHistoricalStatesDefined = true;
                }
                else {
                    this._historicalStates = [];
                    this._isHistoricalStatesDefined = true;
                }
            }
            else {
                if (source.columns !== undefined) {
                    this._columns = source.columns;
                    this._isColumnsDefined = true;
                }
                else {
                    this._columns = [];
                    this._isColumnsDefined = true;
                }
                
                if (source.rows !== undefined) {
                    this._rows = source.rows;
                    this._isRowsDefined = true;
                }
                else {
                    this._rows = [];
                    this._isRowsDefined = true;
                }
                
                if (source.readonly !== undefined) {
                    this._readonly = source.readonly;
                    this._isReadonlyDefined = true;
                }
                else {
                    this._readonly = false;
                    this._isReadonlyDefined = false;
                }
                
                if (source.conflict !== undefined) {
                    this._conflict = source.conflict;
                    this._isConflictDefined = true;
                }
                else {
                    this._conflict = new ConflictScenario();
                    this._isConflictDefined = true;
                }
                
                if (source.validationMessages !== undefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.state !== undefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = Core.EntityStates.Valid;
                    this._isStateDefined = false;
                }
                
                if (source.historicalStates !== undefined) {
                    this._historicalStates = source.historicalStates;
                    this._isHistoricalStatesDefined = true;
                }
                else {
                    this._historicalStates = [];
                    this._isHistoricalStatesDefined = true;
                }
            }
        }
        else {
            this._columns = [];
            this._isColumnsDefined = true;
        
            this._rows = [];
            this._isRowsDefined = true;
        
            this._readonly = false;
            this._isReadonlyDefined = false;
        
            this._conflict = new ConflictScenario();
            this._isConflictDefined = true;
        
            this._validationMessages = [];
            this._isValidationMessagesDefined = true;
        
            this._state = Core.EntityStates.Valid;
            this._isStateDefined = false;
        
            this._historicalStates = [];
            this._isHistoricalStatesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Grid.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isColumnsDefined && this.columns !== undefined)
            definedProperties.push('columns');
        if (this.isRowsDefined && this.rows !== undefined)
            definedProperties.push('rows');
        if (this.isReadonlyDefined && this.readonly !== undefined)
            definedProperties.push('readonly');
        if (this.isConflictDefined && this.conflict !== undefined)
            definedProperties.push('conflict');
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined)
            definedProperties.push('validationMessages');
        if (this.isStateDefined && this.state !== undefined)
            definedProperties.push('state');
        if (this.isHistoricalStatesDefined && this.historicalStates !== undefined)
            definedProperties.push('historicalStates');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'columns':
                return this.columns;
            case 'rows':
                return this.rows;
            case 'readonly':
                return this.readonly;
            case 'conflict':
                return this.conflict;
            case 'validationMessages':
                return this.validationMessages;
            case 'state':
                return this.state;
            case 'historicalStates':
                return this.historicalStates;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isColumnsDefined && this.columns !== undefined) {
            if (this.columns !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('columns', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.columns.length));
                for (let i = 0; i < this.columns.length; i++) {
                    const item = this.columns[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('columns', 'null'));
            }
        }
        
        if (this.isRowsDefined && this.rows !== undefined) {
            if (this.rows !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('rows', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.rows.length));
                for (let i = 0; i < this.rows.length; i++) {
                    const item = this.rows[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('rows', 'null'));
            }
        }
        
        if (this.isReadonlyDefined && this.readonly !== undefined) {
            if (this.readonly !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('readonly', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.readonly);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('readonly', 'null'));
            }
        }
        
        if (this.isConflictDefined && this.conflict !== undefined) {
            if (this.conflict !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('conflict', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.conflict.getModelName(), this.conflict.getDefinedPropertyNames().length));
                this.conflict.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('conflict', 'null'));
            }
        }
        
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            if (this.validationMessages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validationMessages.length));
                for (let i = 0; i < this.validationMessages.length; i++) {
                    const item = this.validationMessages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'null'));
            }
        }
        
        if (this.isStateDefined && this.state !== undefined) {
            if (this.state !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.state);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'null'));
            }
        }
        
        if (this.isHistoricalStatesDefined && this.historicalStates !== undefined) {
            if (this.historicalStates !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('historicalStates', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.historicalStates.length));
                for (let i = 0; i < this.historicalStates.length; i++) {
                    const item = this.historicalStates[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('historicalStates', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'columns':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.columns = [];
                
                    let item: Column = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Column.modelName) {
                                item = new Column();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Column.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'columns' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'columns'`);
                        }
                
                        this.columns.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'columns' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'columns'`);
                }
        
                return true;
            case 'rows':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.rows = [];
                
                    let item: Row = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Row.modelName) {
                                item = new Row();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Row.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'rows' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'rows'`);
                        }
                
                        this.rows.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'rows' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'rows'`);
                }
        
                return true;
            case 'readonly':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.readonly = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'readonly' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'readonly'`);
                }
        
                return true;
            case 'conflict':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ConflictScenario.modelName) {
                        this.conflict = new ConflictScenario();
                        this.conflict.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConflictScenario.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'conflict' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'conflict'`);
                }
        
                return true;
            case 'validationMessages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validationMessages = [];
                
                    let item: ValidationMessage = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ValidationMessage.modelName) {
                                item = new ValidationMessage();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ValidationMessage.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validationMessages' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validationMessages'`);
                        }
                
                        this.validationMessages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validationMessages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validationMessages'`);
                }
        
                return true;
            case 'state':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.state = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'state' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'state'`);
                }
        
                return true;
            case 'historicalStates':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.historicalStates = [];
                
                    let item: HistoricalState = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === HistoricalState.modelName) {
                                item = new HistoricalState();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${HistoricalState.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'historicalStates' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'historicalStates'`);
                        }
                
                        this.historicalStates.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'historicalStates' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'historicalStates'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // columns
        if (this.isColumnsDefined && this.columns !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.columns === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.columns.length);
            
                for (const item of this.columns) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // rows
        if (this.isRowsDefined && this.rows !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.rows === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.rows.length);
            
                for (const item of this.rows) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // readonly
        if (this.isReadonlyDefined && this.readonly !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.readonly);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // conflict
        if (this.isConflictDefined && this.conflict !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.conflict === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.conflict.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validationMessages
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validationMessages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validationMessages.length);
            
                for (const item of this.validationMessages) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // state
        if (this.isStateDefined && this.state !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.state);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // historicalStates
        if (this.isHistoricalStatesDefined && this.historicalStates !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.historicalStates === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.historicalStates.length);
            
                for (const item of this.historicalStates) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // columns
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Column;
            
                    this.columns = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Column();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.columns.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // rows
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Row;
            
                    this.rows = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Row();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.rows.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // readonly
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.readonly = reader.readBooleanValue();
        }
        
        // conflict
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.conflict = new ConflictScenario();
                    this.conflict.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // validationMessages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ValidationMessage;
            
                    this.validationMessages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ValidationMessage();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validationMessages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // state
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.state = reader.readEnumValue();
        }
        
        // historicalStates
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: HistoricalState;
            
                    this.historicalStates = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new HistoricalState();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.historicalStates.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Grid {
        // Method Implementation: simple
        const schema = GridZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: GridZodSchemaShape): Grid {
        // Method Implementation: parseZodSchema
        return new Grid({
            columns: schema.columns?.map(i => Column.parseZodSchema(i)),
            rows: schema.rows?.map(i => Row.parseZodSchema(i)),
            readonly: schema.readonly,
            conflict: schema.conflict && ConflictScenario.parseZodSchema(schema.conflict),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            state: (() => {
            if (!schema.state)
                return;
        
            switch (schema.state) {
                case 'Valid':
                    return Core.EntityStates.Valid;
                case 'Invalid':
                    return Core.EntityStates.Invalid;
                case 'Incomplete':
                    return Core.EntityStates.Incomplete;
                default:
                    throw new Error(`Enum type "schema.state" not recognized`);
            }
        })(),
            historicalStates: schema.historicalStates?.map(i => HistoricalState.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            columns: this.isColumnsDefined ? (this.columns?.map(item => item)) : undefined,
            rows: this.isRowsDefined ? (this.rows?.map(item => item)) : undefined,
            readonly: this.isReadonlyDefined ? (this.readonly) : undefined,
            conflict: this.isConflictDefined ? (this.conflict) : undefined,
            validationMessages: this.isValidationMessagesDefined ? (this.validationMessages?.map(item => item)) : undefined,
            state: this.isStateDefined ? (Core.EntityStates[this.state]) : undefined,
            historicalStates: this.isHistoricalStatesDefined ? (this.historicalStates?.map(item => item)) : undefined,
        };
    }

    public clone(): Grid {
        // Method Implementation: clone
        return new Grid({
            columns: this.isColumnsDefined ? this.columns?.map(i => i.clone()) : undefined,
            rows: this.isRowsDefined ? this.rows?.map(i => i.clone()) : undefined,
            readonly: this.isReadonlyDefined ? this.readonly : undefined,
            conflict: this.isConflictDefined ? this.conflict.clone() : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            state: this.isStateDefined ? this.state : undefined,
            historicalStates: this.isHistoricalStatesDefined ? this.historicalStates?.map(i => i.clone()) : undefined
        });
    }
}

export class HistoricalState implements IHistoricalState {
    public static readonly modelName: string = 'HistoricalState';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'description' ];

    public get modelName(): string {
        // Method Implementation: simple
        return HistoricalState.modelName;
    }

    private _description: string;

    public get description(): string {
        // Method Implementation: simple
        return this._description;
    }

    public set description(value: string) {
        // Method Implementation: simple
        this._description = value;
        this._isDescriptionDefined = true;
    }

    private _isDescriptionDefined: boolean;

    public get isDescriptionDefined(): boolean {
        // Method Implementation: simple
        return this._isDescriptionDefined;
    }

    public constructor();
    public constructor(source: IHistoricalState);
    public constructor(source: Partial<Pick<HistoricalState, 'description'>>);
    public constructor(source?: IHistoricalState | Partial<Pick<HistoricalState, 'description'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isHistoricalStateModel(source)) {
                if (source.isDescriptionDefined) {
                    this._description = source.description;
                    this._isDescriptionDefined = true;
                }
                else {
                    this._description = '';
                    this._isDescriptionDefined = false;
                }
            }
            else {
                if (source.description !== undefined) {
                    this._description = source.description;
                    this._isDescriptionDefined = true;
                }
                else {
                    this._description = '';
                    this._isDescriptionDefined = false;
                }
            }
        }
        else {
            this._description = '';
            this._isDescriptionDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return HistoricalState.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isDescriptionDefined && this.description !== undefined)
            definedProperties.push('description');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'description':
                return this.description;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isDescriptionDefined && this.description !== undefined) {
            if (this.description !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('description', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.description);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('description', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'description':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.description = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'description' cannot be null`);
                    this.description = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'description'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // description
        if (this.isDescriptionDefined && this.description !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.description === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.description);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // description
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.description = <any>null;
                    break;
                case 1:
                    this.description = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): HistoricalState {
        // Method Implementation: simple
        const schema = HistoricalStateZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: HistoricalStateZodSchemaShape): HistoricalState {
        // Method Implementation: parseZodSchema
        return new HistoricalState({
            description: schema.description
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            description: this.isDescriptionDefined ? ((this.description === null ? undefined : this.description)) : undefined,
        };
    }

    public clone(): HistoricalState {
        // Method Implementation: clone
        return new HistoricalState({
            description: this.isDescriptionDefined ? this.description : undefined
        });
    }
}

export class Column implements IColumn {
    public static readonly modelName: string = 'Column';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'displayName', 'helpText', 'source', 'fieldType', 'visible', 'customizable', 'pinned', 'variant', 'group', 'groupDisplay', 'sortable', 'sortDirection', 'sortIndex', 'type', 'answers', 'fastDataEntry' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Column.modelName;
    }

    private _name: string;

    private _displayName: string;

    private _helpText: string;

    private _source: string;

    private _fieldType: FieldTypes;

    private _visible: boolean;

    private _customizable: boolean;

    private _pinned: ModelGrid.PinningLocations;

    private _variant: string;

    private _group: string;

    private _groupDisplay: ModelGrid.GroupDisplays;

    private _sortable: boolean;

    private _sortDirection: ModelGrid.SortingDirections;

    private _sortIndex: number;

    private _type: ModelGrid.ColumnTypes;

    private _answers: ColumnAnswer[];

    private _fastDataEntry: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get displayName(): string {
        // Method Implementation: simple
        return this._displayName;
    }

    public get helpText(): string {
        // Method Implementation: simple
        return this._helpText;
    }

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get fieldType(): FieldTypes {
        // Method Implementation: simple
        return this._fieldType;
    }

    public get visible(): boolean {
        // Method Implementation: simple
        return this._visible;
    }

    public get customizable(): boolean {
        // Method Implementation: simple
        return this._customizable;
    }

    public get pinned(): ModelGrid.PinningLocations {
        // Method Implementation: simple
        return this._pinned;
    }

    public get variant(): string {
        // Method Implementation: simple
        return this._variant;
    }

    public get group(): string {
        // Method Implementation: simple
        return this._group;
    }

    public get groupDisplay(): ModelGrid.GroupDisplays {
        // Method Implementation: simple
        return this._groupDisplay;
    }

    public get sortable(): boolean {
        // Method Implementation: simple
        return this._sortable;
    }

    public get sortDirection(): ModelGrid.SortingDirections {
        // Method Implementation: simple
        return this._sortDirection;
    }

    public get sortIndex(): number {
        // Method Implementation: simple
        return this._sortIndex;
    }

    public get type(): ModelGrid.ColumnTypes {
        // Method Implementation: simple
        return this._type;
    }

    public get answers(): ColumnAnswer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public get fastDataEntry(): string {
        // Method Implementation: simple
        return this._fastDataEntry;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set displayName(value: string) {
        // Method Implementation: simple
        this._displayName = value;
        this._isDisplayNameDefined = true;
    }

    public set helpText(value: string) {
        // Method Implementation: simple
        this._helpText = value;
        this._isHelpTextDefined = true;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set fieldType(value: FieldTypes) {
        // Method Implementation: simple
        this._fieldType = value;
        this._isFieldTypeDefined = true;
    }

    public set visible(value: boolean) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set customizable(value: boolean) {
        // Method Implementation: simple
        this._customizable = value;
        this._isCustomizableDefined = true;
    }

    public set pinned(value: ModelGrid.PinningLocations) {
        // Method Implementation: simple
        this._pinned = value;
        this._isPinnedDefined = true;
    }

    public set variant(value: string) {
        // Method Implementation: simple
        this._variant = value;
        this._isVariantDefined = true;
    }

    public set group(value: string) {
        // Method Implementation: simple
        this._group = value;
        this._isGroupDefined = true;
    }

    public set groupDisplay(value: ModelGrid.GroupDisplays) {
        // Method Implementation: simple
        this._groupDisplay = value;
        this._isGroupDisplayDefined = true;
    }

    public set sortable(value: boolean) {
        // Method Implementation: simple
        this._sortable = value;
        this._isSortableDefined = true;
    }

    public set sortDirection(value: ModelGrid.SortingDirections) {
        // Method Implementation: simple
        this._sortDirection = value;
        this._isSortDirectionDefined = true;
    }

    public set sortIndex(value: number) {
        // Method Implementation: simple
        this._sortIndex = value;
        this._isSortIndexDefined = true;
    }

    public set type(value: ModelGrid.ColumnTypes) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    public set answers(value: ColumnAnswer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    public set fastDataEntry(value: string) {
        // Method Implementation: simple
        this._fastDataEntry = value;
        this._isFastDataEntryDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDisplayNameDefined: boolean;

    private _isHelpTextDefined: boolean;

    private _isSourceDefined: boolean;

    private _isFieldTypeDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isCustomizableDefined: boolean;

    private _isPinnedDefined: boolean;

    private _isVariantDefined: boolean;

    private _isGroupDefined: boolean;

    private _isGroupDisplayDefined: boolean;

    private _isSortableDefined: boolean;

    private _isSortDirectionDefined: boolean;

    private _isSortIndexDefined: boolean;

    private _isTypeDefined: boolean;

    private _isAnswersDefined: boolean;

    private _isFastDataEntryDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDisplayNameDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayNameDefined;
    }

    public get isHelpTextDefined(): boolean {
        // Method Implementation: simple
        return this._isHelpTextDefined;
    }

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isFieldTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isFieldTypeDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isCustomizableDefined(): boolean {
        // Method Implementation: simple
        return this._isCustomizableDefined;
    }

    public get isPinnedDefined(): boolean {
        // Method Implementation: simple
        return this._isPinnedDefined;
    }

    public get isVariantDefined(): boolean {
        // Method Implementation: simple
        return this._isVariantDefined;
    }

    public get isGroupDefined(): boolean {
        // Method Implementation: simple
        return this._isGroupDefined;
    }

    public get isGroupDisplayDefined(): boolean {
        // Method Implementation: simple
        return this._isGroupDisplayDefined;
    }

    public get isSortableDefined(): boolean {
        // Method Implementation: simple
        return this._isSortableDefined;
    }

    public get isSortDirectionDefined(): boolean {
        // Method Implementation: simple
        return this._isSortDirectionDefined;
    }

    public get isSortIndexDefined(): boolean {
        // Method Implementation: simple
        return this._isSortIndexDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public get isFastDataEntryDefined(): boolean {
        // Method Implementation: simple
        return this._isFastDataEntryDefined;
    }

    public constructor();
    public constructor(source: IColumn);
    public constructor(source: Partial<Pick<Column, 'name' | 'displayName' | 'helpText' | 'source' | 'fieldType' | 'visible' | 'customizable' | 'pinned' | 'variant' | 'group' | 'groupDisplay' | 'sortable' | 'sortDirection' | 'sortIndex' | 'type' | 'answers' | 'fastDataEntry'>>);
    public constructor(source?: IColumn | Partial<Pick<Column, 'name' | 'displayName' | 'helpText' | 'source' | 'fieldType' | 'visible' | 'customizable' | 'pinned' | 'variant' | 'group' | 'groupDisplay' | 'sortable' | 'sortDirection' | 'sortIndex' | 'type' | 'answers' | 'fastDataEntry'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isColumnModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDisplayNameDefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.isHelpTextDefined) {
                    this._helpText = source.helpText;
                    this._isHelpTextDefined = true;
                }
                else {
                    this._helpText = '';
                    this._isHelpTextDefined = false;
                }
                
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isFieldTypeDefined) {
                    this._fieldType = source.fieldType;
                    this._isFieldTypeDefined = true;
                }
                else {
                    this._fieldType = FieldTypes.Unassigned;
                    this._isFieldTypeDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.isCustomizableDefined) {
                    this._customizable = source.customizable;
                    this._isCustomizableDefined = true;
                }
                else {
                    this._customizable = false;
                    this._isCustomizableDefined = false;
                }
                
                if (source.isPinnedDefined) {
                    this._pinned = source.pinned;
                    this._isPinnedDefined = true;
                }
                else {
                    this._pinned = ModelGrid.PinningLocations.Unassigned;
                    this._isPinnedDefined = false;
                }
                
                if (source.isVariantDefined) {
                    this._variant = source.variant;
                    this._isVariantDefined = true;
                }
                else {
                    this._variant = '';
                    this._isVariantDefined = false;
                }
                
                if (source.isGroupDefined) {
                    this._group = source.group;
                    this._isGroupDefined = true;
                }
                else {
                    this._group = '';
                    this._isGroupDefined = false;
                }
                
                if (source.isGroupDisplayDefined) {
                    this._groupDisplay = source.groupDisplay;
                    this._isGroupDisplayDefined = true;
                }
                else {
                    this._groupDisplay = ModelGrid.GroupDisplays.Unassigned;
                    this._isGroupDisplayDefined = false;
                }
                
                if (source.isSortableDefined) {
                    this._sortable = source.sortable;
                    this._isSortableDefined = true;
                }
                else {
                    this._sortable = false;
                    this._isSortableDefined = false;
                }
                
                if (source.isSortDirectionDefined) {
                    this._sortDirection = source.sortDirection;
                    this._isSortDirectionDefined = true;
                }
                else {
                    this._sortDirection = ModelGrid.SortingDirections.Unassigned;
                    this._isSortDirectionDefined = false;
                }
                
                if (source.isSortIndexDefined) {
                    this._sortIndex = source.sortIndex;
                    this._isSortIndexDefined = true;
                }
                else {
                    this._sortIndex = 0;
                    this._isSortIndexDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = ModelGrid.ColumnTypes.String;
                    this._isTypeDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
                
                if (source.isFastDataEntryDefined) {
                    this._fastDataEntry = source.fastDataEntry;
                    this._isFastDataEntryDefined = true;
                }
                else {
                    this._fastDataEntry = '';
                    this._isFastDataEntryDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.displayName !== undefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.helpText !== undefined) {
                    this._helpText = source.helpText;
                    this._isHelpTextDefined = true;
                }
                else {
                    this._helpText = '';
                    this._isHelpTextDefined = false;
                }
                
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.fieldType !== undefined) {
                    this._fieldType = source.fieldType;
                    this._isFieldTypeDefined = true;
                }
                else {
                    this._fieldType = FieldTypes.Unassigned;
                    this._isFieldTypeDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.customizable !== undefined) {
                    this._customizable = source.customizable;
                    this._isCustomizableDefined = true;
                }
                else {
                    this._customizable = false;
                    this._isCustomizableDefined = false;
                }
                
                if (source.pinned !== undefined) {
                    this._pinned = source.pinned;
                    this._isPinnedDefined = true;
                }
                else {
                    this._pinned = ModelGrid.PinningLocations.Unassigned;
                    this._isPinnedDefined = false;
                }
                
                if (source.variant !== undefined) {
                    this._variant = source.variant;
                    this._isVariantDefined = true;
                }
                else {
                    this._variant = '';
                    this._isVariantDefined = false;
                }
                
                if (source.group !== undefined) {
                    this._group = source.group;
                    this._isGroupDefined = true;
                }
                else {
                    this._group = '';
                    this._isGroupDefined = false;
                }
                
                if (source.groupDisplay !== undefined) {
                    this._groupDisplay = source.groupDisplay;
                    this._isGroupDisplayDefined = true;
                }
                else {
                    this._groupDisplay = ModelGrid.GroupDisplays.Unassigned;
                    this._isGroupDisplayDefined = false;
                }
                
                if (source.sortable !== undefined) {
                    this._sortable = source.sortable;
                    this._isSortableDefined = true;
                }
                else {
                    this._sortable = false;
                    this._isSortableDefined = false;
                }
                
                if (source.sortDirection !== undefined) {
                    this._sortDirection = source.sortDirection;
                    this._isSortDirectionDefined = true;
                }
                else {
                    this._sortDirection = ModelGrid.SortingDirections.Unassigned;
                    this._isSortDirectionDefined = false;
                }
                
                if (source.sortIndex !== undefined) {
                    this._sortIndex = source.sortIndex;
                    this._isSortIndexDefined = true;
                }
                else {
                    this._sortIndex = 0;
                    this._isSortIndexDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = ModelGrid.ColumnTypes.String;
                    this._isTypeDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
                
                if (source.fastDataEntry !== undefined) {
                    this._fastDataEntry = source.fastDataEntry;
                    this._isFastDataEntryDefined = true;
                }
                else {
                    this._fastDataEntry = '';
                    this._isFastDataEntryDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._displayName = '';
            this._isDisplayNameDefined = false;
        
            this._helpText = '';
            this._isHelpTextDefined = false;
        
            this._source = '';
            this._isSourceDefined = false;
        
            this._fieldType = FieldTypes.Unassigned;
            this._isFieldTypeDefined = false;
        
            this._visible = false;
            this._isVisibleDefined = false;
        
            this._customizable = false;
            this._isCustomizableDefined = false;
        
            this._pinned = ModelGrid.PinningLocations.Unassigned;
            this._isPinnedDefined = false;
        
            this._variant = '';
            this._isVariantDefined = false;
        
            this._group = '';
            this._isGroupDefined = false;
        
            this._groupDisplay = ModelGrid.GroupDisplays.Unassigned;
            this._isGroupDisplayDefined = false;
        
            this._sortable = false;
            this._isSortableDefined = false;
        
            this._sortDirection = ModelGrid.SortingDirections.Unassigned;
            this._isSortDirectionDefined = false;
        
            this._sortIndex = 0;
            this._isSortIndexDefined = false;
        
            this._type = ModelGrid.ColumnTypes.String;
            this._isTypeDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        
            this._fastDataEntry = '';
            this._isFastDataEntryDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Column.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDisplayNameDefined && this.displayName !== undefined)
            definedProperties.push('displayName');
        if (this.isHelpTextDefined && this.helpText !== undefined)
            definedProperties.push('helpText');
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isFieldTypeDefined && this.fieldType !== undefined)
            definedProperties.push('fieldType');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isCustomizableDefined && this.customizable !== undefined)
            definedProperties.push('customizable');
        if (this.isPinnedDefined && this.pinned !== undefined)
            definedProperties.push('pinned');
        if (this.isVariantDefined && this.variant !== undefined)
            definedProperties.push('variant');
        if (this.isGroupDefined && this.group !== undefined)
            definedProperties.push('group');
        if (this.isGroupDisplayDefined && this.groupDisplay !== undefined)
            definedProperties.push('groupDisplay');
        if (this.isSortableDefined && this.sortable !== undefined)
            definedProperties.push('sortable');
        if (this.isSortDirectionDefined && this.sortDirection !== undefined)
            definedProperties.push('sortDirection');
        if (this.isSortIndexDefined && this.sortIndex !== undefined)
            definedProperties.push('sortIndex');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        if (this.isFastDataEntryDefined && this.fastDataEntry !== undefined)
            definedProperties.push('fastDataEntry');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'displayName':
                return this.displayName;
            case 'helpText':
                return this.helpText;
            case 'source':
                return this.source;
            case 'fieldType':
                return this.fieldType;
            case 'visible':
                return this.visible;
            case 'customizable':
                return this.customizable;
            case 'pinned':
                return this.pinned;
            case 'variant':
                return this.variant;
            case 'group':
                return this.group;
            case 'groupDisplay':
                return this.groupDisplay;
            case 'sortable':
                return this.sortable;
            case 'sortDirection':
                return this.sortDirection;
            case 'sortIndex':
                return this.sortIndex;
            case 'type':
                return this.type;
            case 'answers':
                return this.answers;
            case 'fastDataEntry':
                return this.fastDataEntry;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            if (this.displayName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'null'));
            }
        }
        
        if (this.isHelpTextDefined && this.helpText !== undefined) {
            if (this.helpText !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('helpText', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.helpText);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('helpText', 'null'));
            }
        }
        
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isFieldTypeDefined && this.fieldType !== undefined) {
            if (this.fieldType !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fieldType', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.fieldType);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fieldType', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isCustomizableDefined && this.customizable !== undefined) {
            if (this.customizable !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('customizable', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.customizable);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('customizable', 'null'));
            }
        }
        
        if (this.isPinnedDefined && this.pinned !== undefined) {
            if (this.pinned !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('pinned', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.pinned);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('pinned', 'null'));
            }
        }
        
        if (this.isVariantDefined && this.variant !== undefined) {
            if (this.variant !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variant', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.variant);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variant', 'null'));
            }
        }
        
        if (this.isGroupDefined && this.group !== undefined) {
            if (this.group !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('group', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.group);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('group', 'null'));
            }
        }
        
        if (this.isGroupDisplayDefined && this.groupDisplay !== undefined) {
            if (this.groupDisplay !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groupDisplay', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.groupDisplay);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groupDisplay', 'null'));
            }
        }
        
        if (this.isSortableDefined && this.sortable !== undefined) {
            if (this.sortable !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortable', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.sortable);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortable', 'null'));
            }
        }
        
        if (this.isSortDirectionDefined && this.sortDirection !== undefined) {
            if (this.sortDirection !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortDirection', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.sortDirection);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortDirection', 'null'));
            }
        }
        
        if (this.isSortIndexDefined && this.sortIndex !== undefined) {
            if (this.sortIndex !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortIndex', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.sortIndex);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortIndex', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
        
        if (this.isFastDataEntryDefined && this.fastDataEntry !== undefined) {
            if (this.fastDataEntry !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fastDataEntry', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.fastDataEntry);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fastDataEntry', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'displayName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayName' cannot be null`);
                    this.displayName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayName'`);
                }
        
                return true;
            case 'helpText':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.helpText = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'helpText' cannot be null`);
                    this.helpText = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'helpText'`);
                }
        
                return true;
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'fieldType':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.fieldType = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'fieldType' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'fieldType'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.visible = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'visible' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'customizable':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.customizable = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'customizable' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'customizable'`);
                }
        
                return true;
            case 'pinned':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.pinned = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'pinned' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'pinned'`);
                }
        
                return true;
            case 'variant':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.variant = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'variant' cannot be null`);
                    this.variant = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'variant'`);
                }
        
                return true;
            case 'group':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.group = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'group' cannot be null`);
                    this.group = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'group'`);
                }
        
                return true;
            case 'groupDisplay':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.groupDisplay = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'groupDisplay' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'groupDisplay'`);
                }
        
                return true;
            case 'sortable':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.sortable = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'sortable' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'sortable'`);
                }
        
                return true;
            case 'sortDirection':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.sortDirection = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'sortDirection' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'sortDirection'`);
                }
        
                return true;
            case 'sortIndex':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.sortIndex = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.sortIndex = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'sortIndex' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'sortIndex'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.type = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'type' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: ColumnAnswer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ColumnAnswer.modelName) {
                                item = new ColumnAnswer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ColumnAnswer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            case 'fastDataEntry':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.fastDataEntry = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'fastDataEntry' cannot be null`);
                    this.fastDataEntry = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'fastDataEntry'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayName
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // helpText
        if (this.isHelpTextDefined && this.helpText !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.helpText === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.helpText);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // fieldType
        if (this.isFieldTypeDefined && this.fieldType !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.fieldType);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.visible);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // customizable
        if (this.isCustomizableDefined && this.customizable !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.customizable);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // pinned
        if (this.isPinnedDefined && this.pinned !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.pinned);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // variant
        if (this.isVariantDefined && this.variant !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.variant === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.variant);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // group
        if (this.isGroupDefined && this.group !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.group === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.group);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // groupDisplay
        if (this.isGroupDisplayDefined && this.groupDisplay !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.groupDisplay);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // sortable
        if (this.isSortableDefined && this.sortable !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.sortable);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // sortDirection
        if (this.isSortDirectionDefined && this.sortDirection !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.sortDirection);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // sortIndex
        if (this.isSortIndexDefined && this.sortIndex !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.sortIndex);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.type);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // fastDataEntry
        if (this.isFastDataEntryDefined && this.fastDataEntry !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.fastDataEntry === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.fastDataEntry);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayName = <any>null;
                    break;
                case 1:
                    this.displayName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // helpText
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.helpText = <any>null;
                    break;
                case 1:
                    this.helpText = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // fieldType
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.fieldType = reader.readEnumValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.visible = reader.readBooleanValue();
        }
        
        // customizable
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.customizable = reader.readBooleanValue();
        }
        
        // pinned
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.pinned = reader.readEnumValue();
        }
        
        // variant
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.variant = <any>null;
                    break;
                case 1:
                    this.variant = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // group
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.group = <any>null;
                    break;
                case 1:
                    this.group = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // groupDisplay
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.groupDisplay = reader.readEnumValue();
        }
        
        // sortable
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.sortable = reader.readBooleanValue();
        }
        
        // sortDirection
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.sortDirection = reader.readEnumValue();
        }
        
        // sortIndex
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.sortIndex = reader.readDoubleValue();
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.type = reader.readEnumValue();
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ColumnAnswer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ColumnAnswer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // fastDataEntry
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.fastDataEntry = <any>null;
                    break;
                case 1:
                    this.fastDataEntry = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Column {
        // Method Implementation: simple
        const schema = ColumnZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ColumnZodSchemaShape): Column {
        // Method Implementation: parseZodSchema
        return new Column({
            name: schema.name,
            displayName: schema.displayName,
            helpText: schema.helpText,
            source: schema.source,
            fieldType: (() => {
            if (!schema.fieldType)
                return;
        
            switch (schema.fieldType) {
                case 'Unassigned':
                    return FieldTypes.Unassigned;
                case 'String':
                    return FieldTypes.String;
                case 'Numeric':
                    return FieldTypes.Numeric;
                case 'Boolean':
                    return FieldTypes.Boolean;
                case 'StringEnumerable':
                    return FieldTypes.StringEnumerable;
                case 'NumericEnumerable':
                    return FieldTypes.NumericEnumerable;
                case 'MultiString':
                    return FieldTypes.MultiString;
                case 'MultiNumeric':
                    return FieldTypes.MultiNumeric;
                default:
                    throw new Error(`Enum type "schema.fieldType" not recognized`);
            }
        })(),
            visible: schema.visible,
            customizable: schema.customizable,
            pinned: (() => {
            if (!schema.pinned)
                return;
        
            switch (schema.pinned) {
                case 'Unassigned':
                    return ModelGrid.PinningLocations.Unassigned;
                case 'Left':
                    return ModelGrid.PinningLocations.Left;
                case 'Right':
                    return ModelGrid.PinningLocations.Right;
                default:
                    throw new Error(`Enum type "schema.pinned" not recognized`);
            }
        })(),
            variant: schema.variant,
            group: schema.group,
            groupDisplay: (() => {
            if (!schema.groupDisplay)
                return;
        
            switch (schema.groupDisplay) {
                case 'Unassigned':
                    return ModelGrid.GroupDisplays.Unassigned;
                case 'Open':
                    return ModelGrid.GroupDisplays.Open;
                case 'Closed':
                    return ModelGrid.GroupDisplays.Closed;
                default:
                    throw new Error(`Enum type "schema.groupDisplay" not recognized`);
            }
        })(),
            sortable: schema.sortable,
            sortDirection: (() => {
            if (!schema.sortDirection)
                return;
        
            switch (schema.sortDirection) {
                case 'Unassigned':
                    return ModelGrid.SortingDirections.Unassigned;
                case 'Ascending':
                    return ModelGrid.SortingDirections.Ascending;
                case 'Descending':
                    return ModelGrid.SortingDirections.Descending;
                default:
                    throw new Error(`Enum type "schema.sortDirection" not recognized`);
            }
        })(),
            sortIndex: schema.sortIndex,
            type: (() => {
            if (!schema.type)
                return;
        
            switch (schema.type) {
                case 'String':
                    return ModelGrid.ColumnTypes.String;
                case 'Numeric':
                    return ModelGrid.ColumnTypes.Numeric;
                case 'Boolean':
                    return ModelGrid.ColumnTypes.Boolean;
                default:
                    throw new Error(`Enum type "schema.type" not recognized`);
            }
        })(),
            answers: schema.answers?.map(i => ColumnAnswer.parseZodSchema(i)),
            fastDataEntry: schema.fastDataEntry
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            displayName: this.isDisplayNameDefined ? ((this.displayName === null ? undefined : this.displayName)) : undefined,
            helpText: this.isHelpTextDefined ? ((this.helpText === null ? undefined : this.helpText)) : undefined,
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            fieldType: this.isFieldTypeDefined ? (FieldTypes[this.fieldType]) : undefined,
            visible: this.isVisibleDefined ? (this.visible) : undefined,
            customizable: this.isCustomizableDefined ? (this.customizable) : undefined,
            pinned: this.isPinnedDefined ? (ModelGrid.PinningLocations[this.pinned]) : undefined,
            variant: this.isVariantDefined ? ((this.variant === null ? undefined : this.variant)) : undefined,
            group: this.isGroupDefined ? ((this.group === null ? undefined : this.group)) : undefined,
            groupDisplay: this.isGroupDisplayDefined ? (ModelGrid.GroupDisplays[this.groupDisplay]) : undefined,
            sortable: this.isSortableDefined ? (this.sortable) : undefined,
            sortDirection: this.isSortDirectionDefined ? (ModelGrid.SortingDirections[this.sortDirection]) : undefined,
            sortIndex: this.isSortIndexDefined ? (this.sortIndex) : undefined,
            type: this.isTypeDefined ? (ModelGrid.ColumnTypes[this.type]) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
            fastDataEntry: this.isFastDataEntryDefined ? ((this.fastDataEntry === null ? undefined : this.fastDataEntry)) : undefined,
        };
    }

    public clone(): Column {
        // Method Implementation: clone
        return new Column({
            name: this.isNameDefined ? this.name : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            helpText: this.isHelpTextDefined ? this.helpText : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            fieldType: this.isFieldTypeDefined ? this.fieldType : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            customizable: this.isCustomizableDefined ? this.customizable : undefined,
            pinned: this.isPinnedDefined ? this.pinned : undefined,
            variant: this.isVariantDefined ? this.variant : undefined,
            group: this.isGroupDefined ? this.group : undefined,
            groupDisplay: this.isGroupDisplayDefined ? this.groupDisplay : undefined,
            sortable: this.isSortableDefined ? this.sortable : undefined,
            sortDirection: this.isSortDirectionDefined ? this.sortDirection : undefined,
            sortIndex: this.isSortIndexDefined ? this.sortIndex : undefined,
            type: this.isTypeDefined ? this.type : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined,
            fastDataEntry: this.isFastDataEntryDefined ? this.fastDataEntry : undefined
        });
    }
}

export class ColumnAnswer implements IColumnAnswer {
    public static readonly modelName: string = 'ColumnAnswer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'displayName', 'enabled', 'visible', 'assignmentState' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ColumnAnswer.modelName;
    }

    private _name: string;

    private _displayName: string;

    private _enabled: boolean;

    private _visible: boolean;

    private _assignmentState: Core.OptionStates;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get displayName(): string {
        // Method Implementation: simple
        return this._displayName;
    }

    public get enabled(): boolean {
        // Method Implementation: simple
        return this._enabled;
    }

    public get visible(): boolean {
        // Method Implementation: simple
        return this._visible;
    }

    public get assignmentState(): Core.OptionStates {
        // Method Implementation: simple
        return this._assignmentState;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set displayName(value: string) {
        // Method Implementation: simple
        this._displayName = value;
        this._isDisplayNameDefined = true;
    }

    public set enabled(value: boolean) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set visible(value: boolean) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set assignmentState(value: Core.OptionStates) {
        // Method Implementation: simple
        this._assignmentState = value;
        this._isAssignmentStateDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDisplayNameDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isAssignmentStateDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDisplayNameDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayNameDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isAssignmentStateDefined(): boolean {
        // Method Implementation: simple
        return this._isAssignmentStateDefined;
    }

    public constructor();
    public constructor(source: IColumnAnswer);
    public constructor(source: Partial<Pick<ColumnAnswer, 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>);
    public constructor(source?: IColumnAnswer | Partial<Pick<ColumnAnswer, 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isColumnAnswerModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDisplayNameDefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.isAssignmentStateDefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.OptionStates.Assignable;
                    this._isAssignmentStateDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.displayName !== undefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.assignmentState !== undefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.OptionStates.Assignable;
                    this._isAssignmentStateDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._displayName = '';
            this._isDisplayNameDefined = false;
        
            this._enabled = false;
            this._isEnabledDefined = false;
        
            this._visible = false;
            this._isVisibleDefined = false;
        
            this._assignmentState = Core.OptionStates.Assignable;
            this._isAssignmentStateDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ColumnAnswer.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDisplayNameDefined && this.displayName !== undefined)
            definedProperties.push('displayName');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined)
            definedProperties.push('assignmentState');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'displayName':
                return this.displayName;
            case 'enabled':
                return this.enabled;
            case 'visible':
                return this.visible;
            case 'assignmentState':
                return this.assignmentState;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            if (this.displayName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            if (this.assignmentState !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.assignmentState);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'displayName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayName' cannot be null`);
                    this.displayName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayName'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.enabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'enabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.visible = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'visible' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'assignmentState':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.assignmentState = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'assignmentState' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'assignmentState'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayName
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.enabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.visible);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // assignmentState
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.assignmentState);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayName = <any>null;
                    break;
                case 1:
                    this.displayName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.enabled = reader.readBooleanValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.visible = reader.readBooleanValue();
        }
        
        // assignmentState
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.assignmentState = reader.readEnumValue();
        }
    }

    public static parse(hash: unknown): ColumnAnswer {
        // Method Implementation: simple
        const schema = ColumnAnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ColumnAnswerZodSchemaShape): ColumnAnswer {
        // Method Implementation: parseZodSchema
        return new ColumnAnswer({
            name: schema.name,
            displayName: schema.displayName,
            enabled: schema.enabled,
            visible: schema.visible,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Assignable':
                    return Core.OptionStates.Assignable;
                case 'Conflicting':
                    return Core.OptionStates.Conflicting;
                case 'Blocked':
                    return Core.OptionStates.Blocked;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })()
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            displayName: this.isDisplayNameDefined ? ((this.displayName === null ? undefined : this.displayName)) : undefined,
            enabled: this.isEnabledDefined ? (this.enabled) : undefined,
            visible: this.isVisibleDefined ? (this.visible) : undefined,
            assignmentState: this.isAssignmentStateDefined ? (Core.OptionStates[this.assignmentState]) : undefined,
        };
    }

    public clone(): ColumnAnswer {
        // Method Implementation: clone
        return new ColumnAnswer({
            name: this.isNameDefined ? this.name : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined
        });
    }
}

export class Row implements IRow {
    public static readonly modelName: string = 'Row';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityId', 'validationMessages', 'state', 'fields' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Row.modelName;
    }

    private _entityId: string;

    private _validationMessages: ValidationMessage[];

    private _state: Core.EntityStates;

    private _fields: AField[];

    public get entityId(): string {
        // Method Implementation: simple
        return this._entityId;
    }

    public get validationMessages(): ValidationMessage[] {
        // Method Implementation: simple
        return this._validationMessages;
    }

    public get state(): Core.EntityStates {
        // Method Implementation: simple
        return this._state;
    }

    public get fields(): AField[] {
        // Method Implementation: simple
        return this._fields;
    }

    public set entityId(value: string) {
        // Method Implementation: simple
        this._entityId = value;
        this._isEntityIdDefined = true;
    }

    public set validationMessages(value: ValidationMessage[]) {
        // Method Implementation: simple
        this._validationMessages = value;
        this._isValidationMessagesDefined = true;
    }

    public set state(value: Core.EntityStates) {
        // Method Implementation: simple
        this._state = value;
        this._isStateDefined = true;
    }

    public set fields(value: AField[]) {
        // Method Implementation: simple
        this._fields = value;
        this._isFieldsDefined = true;
    }

    private _isEntityIdDefined: boolean;

    private _isValidationMessagesDefined: boolean;

    private _isStateDefined: boolean;

    private _isFieldsDefined: boolean;

    public get isEntityIdDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityIdDefined;
    }

    public get isValidationMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isValidationMessagesDefined;
    }

    public get isStateDefined(): boolean {
        // Method Implementation: simple
        return this._isStateDefined;
    }

    public get isFieldsDefined(): boolean {
        // Method Implementation: simple
        return this._isFieldsDefined;
    }

    public constructor();
    public constructor(source: IRow);
    public constructor(source: Partial<Pick<Row, 'entityId' | 'validationMessages' | 'state' | 'fields'>>);
    public constructor(source?: IRow | Partial<Pick<Row, 'entityId' | 'validationMessages' | 'state' | 'fields'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isRowModel(source)) {
                if (source.isEntityIdDefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.isValidationMessagesDefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.isStateDefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = Core.EntityStates.Valid;
                    this._isStateDefined = false;
                }
                
                if (source.isFieldsDefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
            }
            else {
                if (source.entityId !== undefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.validationMessages !== undefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.state !== undefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = Core.EntityStates.Valid;
                    this._isStateDefined = false;
                }
                
                if (source.fields !== undefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
            }
        }
        else {
            this._entityId = '';
            this._isEntityIdDefined = false;
        
            this._validationMessages = [];
            this._isValidationMessagesDefined = true;
        
            this._state = Core.EntityStates.Valid;
            this._isStateDefined = false;
        
            this._fields = [];
            this._isFieldsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Row.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityIdDefined && this.entityId !== undefined)
            definedProperties.push('entityId');
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined)
            definedProperties.push('validationMessages');
        if (this.isStateDefined && this.state !== undefined)
            definedProperties.push('state');
        if (this.isFieldsDefined && this.fields !== undefined)
            definedProperties.push('fields');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityId':
                return this.entityId;
            case 'validationMessages':
                return this.validationMessages;
            case 'state':
                return this.state;
            case 'fields':
                return this.fields;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            if (this.entityId !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityId);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'null'));
            }
        }
        
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            if (this.validationMessages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validationMessages.length));
                for (let i = 0; i < this.validationMessages.length; i++) {
                    const item = this.validationMessages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'null'));
            }
        }
        
        if (this.isStateDefined && this.state !== undefined) {
            if (this.state !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.state);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'null'));
            }
        }
        
        if (this.isFieldsDefined && this.fields !== undefined) {
            if (this.fields !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.fields.length));
                for (let i = 0; i < this.fields.length; i++) {
                    const item = this.fields[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityId':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityId = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityId' cannot be null`);
                    this.entityId = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityId'`);
                }
        
                return true;
            case 'validationMessages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validationMessages = [];
                
                    let item: ValidationMessage = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ValidationMessage.modelName) {
                                item = new ValidationMessage();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ValidationMessage.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validationMessages' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validationMessages'`);
                        }
                
                        this.validationMessages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validationMessages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validationMessages'`);
                }
        
                return true;
            case 'state':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.state = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'state' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'state'`);
                }
        
                return true;
            case 'fields':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.fields = [];
                
                    let item: AField = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'fields' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'fields'`);
                        }
                
                        this.fields.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'fields' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'fields'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityId
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityId === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityId);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validationMessages
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validationMessages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validationMessages.length);
            
                for (const item of this.validationMessages) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // state
        if (this.isStateDefined && this.state !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.state);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // fields
        if (this.isFieldsDefined && this.fields !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.fields === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.fields.length);
            
                for (const item of this.fields) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof StringField)
                            writer.writeShortValue(0);
                        else if (item instanceof NumericField)
                            writer.writeShortValue(1);
                        else if (item instanceof BooleanField)
                            writer.writeShortValue(2);
                        else if (item instanceof StringEnumerableField)
                            writer.writeShortValue(3);
                        else if (item instanceof NumericEnumerableField)
                            writer.writeShortValue(4);
                        else if (item instanceof MultiStringField)
                            writer.writeShortValue(5);
                        else if (item instanceof MultiNumericField)
                            writer.writeShortValue(6);
                        else if (item instanceof ChildGridField)
                            writer.writeShortValue(7);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityId
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityId = <any>null;
                    break;
                case 1:
                    this.entityId = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // validationMessages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ValidationMessage;
            
                    this.validationMessages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ValidationMessage();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validationMessages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // state
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.state = reader.readEnumValue();
        }
        
        // fields
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: AField;
            
                    this.fields = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new StringField();
                                        break;
                                    case 1:
                                        item = new NumericField();
                                        break;
                                    case 2:
                                        item = new BooleanField();
                                        break;
                                    case 3:
                                        item = new StringEnumerableField();
                                        break;
                                    case 4:
                                        item = new NumericEnumerableField();
                                        break;
                                    case 5:
                                        item = new MultiStringField();
                                        break;
                                    case 6:
                                        item = new MultiNumericField();
                                        break;
                                    case 7:
                                        item = new ChildGridField();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.fields.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Row {
        // Method Implementation: simple
        const schema = RowZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: RowZodSchemaShape): Row {
        // Method Implementation: parseZodSchema
        return new Row({
            entityId: schema.entityId,
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            state: (() => {
            if (!schema.state)
                return;
        
            switch (schema.state) {
                case 'Valid':
                    return Core.EntityStates.Valid;
                case 'Invalid':
                    return Core.EntityStates.Invalid;
                case 'Incomplete':
                    return Core.EntityStates.Incomplete;
                default:
                    throw new Error(`Enum type "schema.state" not recognized`);
            }
        })(),
            fields: schema.fields?.map(i => AField.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityId: this.isEntityIdDefined ? ((this.entityId === null ? undefined : this.entityId)) : undefined,
            validationMessages: this.isValidationMessagesDefined ? (this.validationMessages?.map(item => item)) : undefined,
            state: this.isStateDefined ? (Core.EntityStates[this.state]) : undefined,
            fields: this.isFieldsDefined ? (this.fields?.map(item => item)) : undefined,
        };
    }

    public clone(): Row {
        // Method Implementation: clone
        return new Row({
            entityId: this.isEntityIdDefined ? this.entityId : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            state: this.isStateDefined ? this.state : undefined,
            fields: this.isFieldsDefined ? this.fields?.map(i => i.clone()) : undefined
        });
    }
}

export abstract class AField implements IField {
    public static readonly modelName: string = 'Field';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'columnName', 'source', 'enabled', 'required', 'validationState', 'validationMessages', 'displayValue', 'assignmentState', 'recommendedDisplayValue' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AField.modelName;
    }

    private _columnName: string;

    private _source: string;

    private _enabled: boolean;

    private _required: boolean;

    private _validationState: FieldValidationStates;

    private _validationMessages: ValidationMessage[];

    private _displayValue: string;

    private _assignmentState: Core.AssignmentStates;

    private _recommendedDisplayValue: string;

    public get columnName(): string {
        // Method Implementation: simple
        return this._columnName;
    }

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get enabled(): boolean {
        // Method Implementation: simple
        return this._enabled;
    }

    public get required(): boolean {
        // Method Implementation: simple
        return this._required;
    }

    public get validationState(): FieldValidationStates {
        // Method Implementation: simple
        return this._validationState;
    }

    public get validationMessages(): ValidationMessage[] {
        // Method Implementation: simple
        return this._validationMessages;
    }

    public get displayValue(): string {
        // Method Implementation: simple
        return this._displayValue;
    }

    public get assignmentState(): Core.AssignmentStates {
        // Method Implementation: simple
        return this._assignmentState;
    }

    public get recommendedDisplayValue(): string {
        // Method Implementation: simple
        return this._recommendedDisplayValue;
    }

    public set columnName(value: string) {
        // Method Implementation: simple
        this._columnName = value;
        this._isColumnNameDefined = true;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set enabled(value: boolean) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set required(value: boolean) {
        // Method Implementation: simple
        this._required = value;
        this._isRequiredDefined = true;
    }

    public set validationState(value: FieldValidationStates) {
        // Method Implementation: simple
        this._validationState = value;
        this._isValidationStateDefined = true;
    }

    public set validationMessages(value: ValidationMessage[]) {
        // Method Implementation: simple
        this._validationMessages = value;
        this._isValidationMessagesDefined = true;
    }

    public set displayValue(value: string) {
        // Method Implementation: simple
        this._displayValue = value;
        this._isDisplayValueDefined = true;
    }

    public set assignmentState(value: Core.AssignmentStates) {
        // Method Implementation: simple
        this._assignmentState = value;
        this._isAssignmentStateDefined = true;
    }

    public set recommendedDisplayValue(value: string) {
        // Method Implementation: simple
        this._recommendedDisplayValue = value;
        this._isRecommendedDisplayValueDefined = true;
    }

    private _isColumnNameDefined: boolean;

    private _isSourceDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isRequiredDefined: boolean;

    private _isValidationStateDefined: boolean;

    private _isValidationMessagesDefined: boolean;

    private _isDisplayValueDefined: boolean;

    private _isAssignmentStateDefined: boolean;

    private _isRecommendedDisplayValueDefined: boolean;

    public get isColumnNameDefined(): boolean {
        // Method Implementation: simple
        return this._isColumnNameDefined;
    }

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isRequiredDefined(): boolean {
        // Method Implementation: simple
        return this._isRequiredDefined;
    }

    public get isValidationStateDefined(): boolean {
        // Method Implementation: simple
        return this._isValidationStateDefined;
    }

    public get isValidationMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isValidationMessagesDefined;
    }

    public get isDisplayValueDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayValueDefined;
    }

    public get isAssignmentStateDefined(): boolean {
        // Method Implementation: simple
        return this._isAssignmentStateDefined;
    }

    public get isRecommendedDisplayValueDefined(): boolean {
        // Method Implementation: simple
        return this._isRecommendedDisplayValueDefined;
    }

    public constructor();
    public constructor(source: IField);
    public constructor(source: Partial<Pick<AField, 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IField | Partial<Pick<AField, 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isFieldModel(source)) {
                if (source.isColumnNameDefined) {
                    this._columnName = source.columnName;
                    this._isColumnNameDefined = true;
                }
                else {
                    this._columnName = '';
                    this._isColumnNameDefined = false;
                }
                
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.isRequiredDefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = false;
                    this._isRequiredDefined = false;
                }
                
                if (source.isValidationStateDefined) {
                    this._validationState = source.validationState;
                    this._isValidationStateDefined = true;
                }
                else {
                    this._validationState = FieldValidationStates.Valid;
                    this._isValidationStateDefined = false;
                }
                
                if (source.isValidationMessagesDefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.isDisplayValueDefined) {
                    this._displayValue = source.displayValue;
                    this._isDisplayValueDefined = true;
                }
                else {
                    this._displayValue = '';
                    this._isDisplayValueDefined = false;
                }
                
                if (source.isAssignmentStateDefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.AssignmentStates.Unassigned;
                    this._isAssignmentStateDefined = false;
                }
                
                if (source.isRecommendedDisplayValueDefined) {
                    this._recommendedDisplayValue = source.recommendedDisplayValue;
                    this._isRecommendedDisplayValueDefined = true;
                }
                else {
                    this._recommendedDisplayValue = '';
                    this._isRecommendedDisplayValueDefined = false;
                }
            }
            else {
                if (source.columnName !== undefined) {
                    this._columnName = source.columnName;
                    this._isColumnNameDefined = true;
                }
                else {
                    this._columnName = '';
                    this._isColumnNameDefined = false;
                }
                
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.required !== undefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = false;
                    this._isRequiredDefined = false;
                }
                
                if (source.validationState !== undefined) {
                    this._validationState = source.validationState;
                    this._isValidationStateDefined = true;
                }
                else {
                    this._validationState = FieldValidationStates.Valid;
                    this._isValidationStateDefined = false;
                }
                
                if (source.validationMessages !== undefined) {
                    this._validationMessages = source.validationMessages;
                    this._isValidationMessagesDefined = true;
                }
                else {
                    this._validationMessages = [];
                    this._isValidationMessagesDefined = true;
                }
                
                if (source.displayValue !== undefined) {
                    this._displayValue = source.displayValue;
                    this._isDisplayValueDefined = true;
                }
                else {
                    this._displayValue = '';
                    this._isDisplayValueDefined = false;
                }
                
                if (source.assignmentState !== undefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.AssignmentStates.Unassigned;
                    this._isAssignmentStateDefined = false;
                }
                
                if (source.recommendedDisplayValue !== undefined) {
                    this._recommendedDisplayValue = source.recommendedDisplayValue;
                    this._isRecommendedDisplayValueDefined = true;
                }
                else {
                    this._recommendedDisplayValue = '';
                    this._isRecommendedDisplayValueDefined = false;
                }
            }
        }
        else {
            this._columnName = '';
            this._isColumnNameDefined = false;
        
            this._source = '';
            this._isSourceDefined = false;
        
            this._enabled = false;
            this._isEnabledDefined = false;
        
            this._required = false;
            this._isRequiredDefined = false;
        
            this._validationState = FieldValidationStates.Valid;
            this._isValidationStateDefined = false;
        
            this._validationMessages = [];
            this._isValidationMessagesDefined = true;
        
            this._displayValue = '';
            this._isDisplayValueDefined = false;
        
            this._assignmentState = Core.AssignmentStates.Unassigned;
            this._isAssignmentStateDefined = false;
        
            this._recommendedDisplayValue = '';
            this._isRecommendedDisplayValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AField.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isColumnNameDefined && this.columnName !== undefined)
            definedProperties.push('columnName');
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isRequiredDefined && this.required !== undefined)
            definedProperties.push('required');
        if (this.isValidationStateDefined && this.validationState !== undefined)
            definedProperties.push('validationState');
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined)
            definedProperties.push('validationMessages');
        if (this.isDisplayValueDefined && this.displayValue !== undefined)
            definedProperties.push('displayValue');
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined)
            definedProperties.push('assignmentState');
        if (this.isRecommendedDisplayValueDefined && this.recommendedDisplayValue !== undefined)
            definedProperties.push('recommendedDisplayValue');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'columnName':
                return this.columnName;
            case 'source':
                return this.source;
            case 'enabled':
                return this.enabled;
            case 'required':
                return this.required;
            case 'validationState':
                return this.validationState;
            case 'validationMessages':
                return this.validationMessages;
            case 'displayValue':
                return this.displayValue;
            case 'assignmentState':
                return this.assignmentState;
            case 'recommendedDisplayValue':
                return this.recommendedDisplayValue;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isColumnNameDefined && this.columnName !== undefined) {
            if (this.columnName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('columnName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.columnName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('columnName', 'null'));
            }
        }
        
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isRequiredDefined && this.required !== undefined) {
            if (this.required !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.required);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'null'));
            }
        }
        
        if (this.isValidationStateDefined && this.validationState !== undefined) {
            if (this.validationState !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationState', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.validationState);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationState', 'null'));
            }
        }
        
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            if (this.validationMessages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validationMessages.length));
                for (let i = 0; i < this.validationMessages.length; i++) {
                    const item = this.validationMessages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validationMessages', 'null'));
            }
        }
        
        if (this.isDisplayValueDefined && this.displayValue !== undefined) {
            if (this.displayValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayValue', 'null'));
            }
        }
        
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            if (this.assignmentState !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.assignmentState);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'null'));
            }
        }
        
        if (this.isRecommendedDisplayValueDefined && this.recommendedDisplayValue !== undefined) {
            if (this.recommendedDisplayValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('recommendedDisplayValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.recommendedDisplayValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('recommendedDisplayValue', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'columnName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.columnName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'columnName' cannot be null`);
                    this.columnName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'columnName'`);
                }
        
                return true;
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.enabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'enabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'required':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.required = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'required' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'required'`);
                }
        
                return true;
            case 'validationState':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.validationState = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'validationState' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validationState'`);
                }
        
                return true;
            case 'validationMessages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validationMessages = [];
                
                    let item: ValidationMessage = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ValidationMessage.modelName) {
                                item = new ValidationMessage();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ValidationMessage.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validationMessages' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validationMessages'`);
                        }
                
                        this.validationMessages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validationMessages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validationMessages'`);
                }
        
                return true;
            case 'displayValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayValue' cannot be null`);
                    this.displayValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayValue'`);
                }
        
                return true;
            case 'assignmentState':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.assignmentState = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'assignmentState' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'assignmentState'`);
                }
        
                return true;
            case 'recommendedDisplayValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.recommendedDisplayValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'recommendedDisplayValue' cannot be null`);
                    this.recommendedDisplayValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'recommendedDisplayValue'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // columnName
        if (this.isColumnNameDefined && this.columnName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.columnName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.columnName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.enabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // required
        if (this.isRequiredDefined && this.required !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.required);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validationState
        if (this.isValidationStateDefined && this.validationState !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.validationState);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validationMessages
        if (this.isValidationMessagesDefined && this.validationMessages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validationMessages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validationMessages.length);
            
                for (const item of this.validationMessages) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayValue
        if (this.isDisplayValueDefined && this.displayValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // assignmentState
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.assignmentState);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // recommendedDisplayValue
        if (this.isRecommendedDisplayValueDefined && this.recommendedDisplayValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.recommendedDisplayValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.recommendedDisplayValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // columnName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.columnName = <any>null;
                    break;
                case 1:
                    this.columnName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.enabled = reader.readBooleanValue();
        }
        
        // required
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.required = reader.readBooleanValue();
        }
        
        // validationState
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.validationState = reader.readEnumValue();
        }
        
        // validationMessages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ValidationMessage;
            
                    this.validationMessages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ValidationMessage();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validationMessages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayValue = <any>null;
                    break;
                case 1:
                    this.displayValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // assignmentState
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.assignmentState = reader.readEnumValue();
        }
        
        // recommendedDisplayValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.recommendedDisplayValue = <any>null;
                    break;
                case 1:
                    this.recommendedDisplayValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AField {
        // Method Implementation: simple
        const schema = FieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: FieldZodSchemaShape): AField {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.fieldType)
        {
            case 'stringField':
            case 'string':
                return StringField.parse(schema);
            case 'numericField':
            case 'numeric':
                return NumericField.parse(schema);
            case 'booleanField':
            case 'boolean':
                return BooleanField.parse(schema);
            case 'stringEnumerableField':
            case 'stringEnumerable':
                return StringEnumerableField.parse(schema);
            case 'numericEnumerableField':
            case 'numericEnumerable':
                return NumericEnumerableField.parse(schema);
            case 'multiStringField':
            case 'multiString':
                return MultiStringField.parse(schema);
            case 'multiNumericField':
            case 'multiNumeric':
                return MultiNumericField.parse(schema);
            case 'childGridField':
            case 'childGrid':
                return ChildGridField.parse(schema);
            default:
                throw new Error(`fieldType of ${ schema.fieldType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            columnName: this.isColumnNameDefined ? ((this.columnName === null ? undefined : this.columnName)) : undefined,
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            enabled: this.isEnabledDefined ? (this.enabled) : undefined,
            required: this.isRequiredDefined ? (this.required) : undefined,
            validationState: this.isValidationStateDefined ? (FieldValidationStates[this.validationState]) : undefined,
            validationMessages: this.isValidationMessagesDefined ? (this.validationMessages?.map(item => item)) : undefined,
            displayValue: this.isDisplayValueDefined ? ((this.displayValue === null ? undefined : this.displayValue)) : undefined,
            assignmentState: this.isAssignmentStateDefined ? (Core.AssignmentStates[this.assignmentState]) : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? ((this.recommendedDisplayValue === null ? undefined : this.recommendedDisplayValue)) : undefined,
        };
    }

    public abstract clone(): AField;
}

export class StringField extends AField implements IStringField {
    public static readonly modelName: string = 'StringField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringField.modelName;
    }

    private _value?: string;

    public get value(): string | undefined {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string | undefined) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringField);
    public constructor(source: Partial<Pick<StringField, 'value' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IStringField | Partial<Pick<StringField, 'value' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringField {
        // Method Implementation: simple
        const schema = StringFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringFieldZodSchemaShape): StringField {
        // Method Implementation: parseZodSchema
        return new StringField({
            columnName: schema.columnName,
            source: schema.source,
            enabled: schema.enabled,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return FieldValidationStates.Valid;
                case 'SoftInvalid':
                    return FieldValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return FieldValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringField {
        // Method Implementation: clone
        return new StringField({
            columnName: this.isColumnNameDefined ? this.columnName : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericField extends AField implements INumericField {
    public static readonly modelName: string = 'NumericField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'precision', 'min', 'max' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericField.modelName;
    }

    private _value?: number;

    private _precision: number;

    private _min: number;

    private _max: number;

    public get value(): number | undefined {
        // Method Implementation: simple
        return this._value;
    }

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get min(): number {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): number {
        // Method Implementation: simple
        return this._max;
    }

    public set value(value: number | undefined) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set min(value: number) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: number) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    private _isValueDefined: boolean;

    private _isPrecisionDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public constructor();
    public constructor(source: INumericField);
    public constructor(source: Partial<Pick<NumericField, 'value' | 'precision' | 'min' | 'max' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: INumericField | Partial<Pick<NumericField, 'value' | 'precision' | 'min' | 'max' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._min = 0;
            this._isMinDefined = false;
        
            this._max = 0;
            this._isMaxDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'precision':
                return this.precision;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.min = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.min = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'min' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.max = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.max = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'max' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.min);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.max);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.min = reader.readDoubleValue();
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.max = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericField {
        // Method Implementation: simple
        const schema = NumericFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericFieldZodSchemaShape): NumericField {
        // Method Implementation: parseZodSchema
        return new NumericField({
            columnName: schema.columnName,
            source: schema.source,
            enabled: schema.enabled,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return FieldValidationStates.Valid;
                case 'SoftInvalid':
                    return FieldValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return FieldValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            value: schema.value,
            precision: schema.precision,
            min: schema.min,
            max: schema.max
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            min: this.isMinDefined ? (this.min) : undefined,
            max: this.isMaxDefined ? (this.max) : undefined,
        };
    }

    public clone(): NumericField {
        // Method Implementation: clone
        return new NumericField({
            columnName: this.isColumnNameDefined ? this.columnName : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            value: this.isValueDefined ? this.value : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined
        });
    }
}

export class BooleanField extends AField implements IBooleanField {
    public static readonly modelName: string = 'BooleanField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return BooleanField.modelName;
    }

    private _value?: boolean;

    public get value(): boolean | undefined {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: boolean | undefined) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IBooleanField);
    public constructor(source: Partial<Pick<BooleanField, 'value' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IBooleanField | Partial<Pick<BooleanField, 'value' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isBooleanFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = false;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...BooleanField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.value = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.value = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): BooleanField {
        // Method Implementation: simple
        const schema = BooleanFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: BooleanFieldZodSchemaShape): BooleanField {
        // Method Implementation: parseZodSchema
        return new BooleanField({
            columnName: schema.columnName,
            source: schema.source,
            enabled: schema.enabled,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return FieldValidationStates.Valid;
                case 'SoftInvalid':
                    return FieldValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return FieldValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'boolean',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): BooleanField {
        // Method Implementation: clone
        return new BooleanField({
            columnName: this.isColumnNameDefined ? this.columnName : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class StringEnumerableField extends AField implements IStringEnumerableField {
    public static readonly modelName: string = 'StringEnumerableField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'allowCustomValue', 'answers' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringEnumerableField.modelName;
    }

    private _value?: string;

    private _allowCustomValue: boolean;

    private _answers: StringAnswer[];

    public get value(): string | undefined {
        // Method Implementation: simple
        return this._value;
    }

    public get allowCustomValue(): boolean {
        // Method Implementation: simple
        return this._allowCustomValue;
    }

    public get answers(): StringAnswer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public set value(value: string | undefined) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set allowCustomValue(value: boolean) {
        // Method Implementation: simple
        this._allowCustomValue = value;
        this._isAllowCustomValueDefined = true;
    }

    public set answers(value: StringAnswer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    private _isValueDefined: boolean;

    private _isAllowCustomValueDefined: boolean;

    private _isAnswersDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isAllowCustomValueDefined(): boolean {
        // Method Implementation: simple
        return this._isAllowCustomValueDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public constructor();
    public constructor(source: IStringEnumerableField);
    public constructor(source: Partial<Pick<StringEnumerableField, 'value' | 'allowCustomValue' | 'answers' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IStringEnumerableField | Partial<Pick<StringEnumerableField, 'value' | 'allowCustomValue' | 'answers' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringEnumerableFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.isAllowCustomValueDefined) {
                    this._allowCustomValue = source.allowCustomValue;
                    this._isAllowCustomValueDefined = true;
                }
                else {
                    this._allowCustomValue = false;
                    this._isAllowCustomValueDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.allowCustomValue !== undefined) {
                    this._allowCustomValue = source.allowCustomValue;
                    this._isAllowCustomValueDefined = true;
                }
                else {
                    this._allowCustomValue = false;
                    this._isAllowCustomValueDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        
            this._allowCustomValue = false;
            this._isAllowCustomValueDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringEnumerableField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined)
            definedProperties.push('allowCustomValue');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'allowCustomValue':
                return this.allowCustomValue;
            case 'answers':
                return this.answers;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined) {
            if (this.allowCustomValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('allowCustomValue', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.allowCustomValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('allowCustomValue', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'allowCustomValue':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.allowCustomValue = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'allowCustomValue' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'allowCustomValue'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: StringAnswer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === StringAnswer.modelName) {
                                item = new StringAnswer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${StringAnswer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // allowCustomValue
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.allowCustomValue);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // allowCustomValue
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.allowCustomValue = reader.readBooleanValue();
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: StringAnswer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new StringAnswer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringEnumerableField {
        // Method Implementation: simple
        const schema = StringEnumerableFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringEnumerableFieldZodSchemaShape): StringEnumerableField {
        // Method Implementation: parseZodSchema
        return new StringEnumerableField({
            columnName: schema.columnName,
            source: schema.source,
            enabled: schema.enabled,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return FieldValidationStates.Valid;
                case 'SoftInvalid':
                    return FieldValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return FieldValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            value: schema.value,
            allowCustomValue: schema.allowCustomValue,
            answers: schema.answers?.map(i => StringAnswer.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'stringEnumerable',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
            allowCustomValue: this.isAllowCustomValueDefined ? (this.allowCustomValue) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
        };
    }

    public clone(): StringEnumerableField {
        // Method Implementation: clone
        return new StringEnumerableField({
            columnName: this.isColumnNameDefined ? this.columnName : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            value: this.isValueDefined ? this.value : undefined,
            allowCustomValue: this.isAllowCustomValueDefined ? this.allowCustomValue : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined
        });
    }
}

export class NumericEnumerableField extends AField implements INumericEnumerableField {
    public static readonly modelName: string = 'NumericEnumerableField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'allowCustomValue', 'answers', 'precision', 'min', 'max' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericEnumerableField.modelName;
    }

    private _value?: number;

    private _allowCustomValue: boolean;

    private _answers: NumericAnswer[];

    private _precision: number;

    private _min: number;

    private _max: number;

    public get value(): number | undefined {
        // Method Implementation: simple
        return this._value;
    }

    public get allowCustomValue(): boolean {
        // Method Implementation: simple
        return this._allowCustomValue;
    }

    public get answers(): NumericAnswer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get min(): number {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): number {
        // Method Implementation: simple
        return this._max;
    }

    public set value(value: number | undefined) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set allowCustomValue(value: boolean) {
        // Method Implementation: simple
        this._allowCustomValue = value;
        this._isAllowCustomValueDefined = true;
    }

    public set answers(value: NumericAnswer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set min(value: number) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: number) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    private _isValueDefined: boolean;

    private _isAllowCustomValueDefined: boolean;

    private _isAnswersDefined: boolean;

    private _isPrecisionDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isAllowCustomValueDefined(): boolean {
        // Method Implementation: simple
        return this._isAllowCustomValueDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public constructor();
    public constructor(source: INumericEnumerableField);
    public constructor(source: Partial<Pick<NumericEnumerableField, 'value' | 'allowCustomValue' | 'answers' | 'precision' | 'min' | 'max' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: INumericEnumerableField | Partial<Pick<NumericEnumerableField, 'value' | 'allowCustomValue' | 'answers' | 'precision' | 'min' | 'max' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericEnumerableFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.isAllowCustomValueDefined) {
                    this._allowCustomValue = source.allowCustomValue;
                    this._isAllowCustomValueDefined = true;
                }
                else {
                    this._allowCustomValue = false;
                    this._isAllowCustomValueDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
                
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.allowCustomValue !== undefined) {
                    this._allowCustomValue = source.allowCustomValue;
                    this._isAllowCustomValueDefined = true;
                }
                else {
                    this._allowCustomValue = false;
                    this._isAllowCustomValueDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
                
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        
            this._allowCustomValue = false;
            this._isAllowCustomValueDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._min = 0;
            this._isMinDefined = false;
        
            this._max = 0;
            this._isMaxDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericEnumerableField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined)
            definedProperties.push('allowCustomValue');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'allowCustomValue':
                return this.allowCustomValue;
            case 'answers':
                return this.answers;
            case 'precision':
                return this.precision;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined) {
            if (this.allowCustomValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('allowCustomValue', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.allowCustomValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('allowCustomValue', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'allowCustomValue':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.allowCustomValue = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'allowCustomValue' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'allowCustomValue'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: NumericAnswer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === NumericAnswer.modelName) {
                                item = new NumericAnswer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${NumericAnswer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.min = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.min = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'min' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.max = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.max = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'max' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // allowCustomValue
        if (this.isAllowCustomValueDefined && this.allowCustomValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.allowCustomValue);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.min);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.max);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
        
        // allowCustomValue
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.allowCustomValue = reader.readBooleanValue();
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: NumericAnswer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new NumericAnswer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.min = reader.readDoubleValue();
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.max = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericEnumerableField {
        // Method Implementation: simple
        const schema = NumericEnumerableFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericEnumerableFieldZodSchemaShape): NumericEnumerableField {
        // Method Implementation: parseZodSchema
        return new NumericEnumerableField({
            columnName: schema.columnName,
            source: schema.source,
            enabled: schema.enabled,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return FieldValidationStates.Valid;
                case 'SoftInvalid':
                    return FieldValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return FieldValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            value: schema.value,
            allowCustomValue: schema.allowCustomValue,
            answers: schema.answers?.map(i => NumericAnswer.parseZodSchema(i)),
            precision: schema.precision,
            min: schema.min,
            max: schema.max
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'numericEnumerable',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
            allowCustomValue: this.isAllowCustomValueDefined ? (this.allowCustomValue) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            min: this.isMinDefined ? (this.min) : undefined,
            max: this.isMaxDefined ? (this.max) : undefined,
        };
    }

    public clone(): NumericEnumerableField {
        // Method Implementation: clone
        return new NumericEnumerableField({
            columnName: this.isColumnNameDefined ? this.columnName : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            value: this.isValueDefined ? this.value : undefined,
            allowCustomValue: this.isAllowCustomValueDefined ? this.allowCustomValue : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined
        });
    }
}

export class MultiStringField extends AField implements IMultiStringField {
    public static readonly modelName: string = 'MultiStringField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'values' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MultiStringField.modelName;
    }

    private _values?: string[];

    public get values(): string[] | undefined {
        // Method Implementation: simple
        return this._values;
    }

    public set values(value: string[] | undefined) {
        // Method Implementation: simple
        this._values = value;
        this._isValuesDefined = true;
    }

    private _isValuesDefined: boolean;

    public get isValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isValuesDefined;
    }

    public constructor();
    public constructor(source: IMultiStringField);
    public constructor(source: Partial<Pick<MultiStringField, 'values' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IMultiStringField | Partial<Pick<MultiStringField, 'values' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMultiStringFieldModel(source)) {
                if (source.isValuesDefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
            else {
                if (source.values !== undefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._values = [];
            this._isValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MultiStringField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValuesDefined && this.values !== undefined)
            definedProperties.push('values');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'values':
                return this.values;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValuesDefined && this.values !== undefined) {
            if (this.values !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.values.length));
                for (let i = 0; i < this.values.length; i++) {
                    const item = this.values[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'values':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.values = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'values' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'values'`);
                        }
                
                        this.values.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'values' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'values'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // values
        if (this.isValuesDefined && this.values !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.values === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.values.length);
            
                for (const item of this.values) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // values
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.values = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.values.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): MultiStringField {
        // Method Implementation: simple
        const schema = MultiStringFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MultiStringFieldZodSchemaShape): MultiStringField {
        // Method Implementation: parseZodSchema
        return new MultiStringField({
            columnName: schema.columnName,
            source: schema.source,
            enabled: schema.enabled,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return FieldValidationStates.Valid;
                case 'SoftInvalid':
                    return FieldValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return FieldValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            values: schema.values?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'multiString',
            ...super.toJSON(),
            values: this.isValuesDefined ? (this.values?.map(item => (item === null ? undefined : item))) : undefined,
        };
    }

    public clone(): MultiStringField {
        // Method Implementation: clone
        return new MultiStringField({
            columnName: this.isColumnNameDefined ? this.columnName : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            values: this.isValuesDefined ? this.values?.map(i => i) : undefined
        });
    }
}

export class MultiNumericField extends AField implements IMultiNumericField {
    public static readonly modelName: string = 'MultiNumericField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'values', 'precision', 'min', 'max' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MultiNumericField.modelName;
    }

    private _values?: number[];

    private _precision: number;

    private _min: number;

    private _max: number;

    public get values(): number[] | undefined {
        // Method Implementation: simple
        return this._values;
    }

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get min(): number {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): number {
        // Method Implementation: simple
        return this._max;
    }

    public set values(value: number[] | undefined) {
        // Method Implementation: simple
        this._values = value;
        this._isValuesDefined = true;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set min(value: number) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: number) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    private _isValuesDefined: boolean;

    private _isPrecisionDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    public get isValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isValuesDefined;
    }

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public constructor();
    public constructor(source: IMultiNumericField);
    public constructor(source: Partial<Pick<MultiNumericField, 'values' | 'precision' | 'min' | 'max' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IMultiNumericField | Partial<Pick<MultiNumericField, 'values' | 'precision' | 'min' | 'max' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMultiNumericFieldModel(source)) {
                if (source.isValuesDefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
                
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
            else {
                if (source.values !== undefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
                
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
            }
        }
        else {
            super();
        
            this._values = [];
            this._isValuesDefined = true;
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._min = 0;
            this._isMinDefined = false;
        
            this._max = 0;
            this._isMaxDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MultiNumericField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValuesDefined && this.values !== undefined)
            definedProperties.push('values');
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'values':
                return this.values;
            case 'precision':
                return this.precision;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValuesDefined && this.values !== undefined) {
            if (this.values !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.values.length));
                for (let i = 0; i < this.values.length; i++) {
                    const item = this.values[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('double'));
                
                        // Implementation: axon/write/double
                        writer.writeDoubleValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'null'));
            }
        }
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'values':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.values = [];
                
                    let item: number = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: double
                        if (arrayItemHeader.type === 'double') {
                            item = reader.readDoubleValue();
                        }
                        else if (arrayItemHeader.type === 'single') {
                            item = reader.readFloatValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'values' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'values'`);
                        }
                
                        this.values.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'values' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'values'`);
                }
        
                return true;
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.min = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.min = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'min' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.max = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.max = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'max' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // values
        if (this.isValuesDefined && this.values !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.values === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.values.length);
            
                for (const item of this.values) {
                    // Item Implementation: double
                    writer.writeDoubleValue(item);
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.min);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.max);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // values
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: number;
            
                    this.values = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: double
                        item = reader.readDoubleValue();
            
                        this.values.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.min = reader.readDoubleValue();
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.max = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): MultiNumericField {
        // Method Implementation: simple
        const schema = MultiNumericFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MultiNumericFieldZodSchemaShape): MultiNumericField {
        // Method Implementation: parseZodSchema
        return new MultiNumericField({
            columnName: schema.columnName,
            source: schema.source,
            enabled: schema.enabled,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return FieldValidationStates.Valid;
                case 'SoftInvalid':
                    return FieldValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return FieldValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            values: schema.values?.map(i => i),
            precision: schema.precision,
            min: schema.min,
            max: schema.max
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'multiNumeric',
            ...super.toJSON(),
            values: this.isValuesDefined ? (this.values?.map(item => item)) : undefined,
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            min: this.isMinDefined ? (this.min) : undefined,
            max: this.isMaxDefined ? (this.max) : undefined,
        };
    }

    public clone(): MultiNumericField {
        // Method Implementation: clone
        return new MultiNumericField({
            columnName: this.isColumnNameDefined ? this.columnName : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            values: this.isValuesDefined ? this.values?.map(i => i) : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined
        });
    }
}

export class ChildGridField extends AField implements IChildGridField {
    public static readonly modelName: string = 'ChildGridField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'grid' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ChildGridField.modelName;
    }

    private _grid: Grid;

    public get grid(): Grid {
        // Method Implementation: simple
        return this._grid;
    }

    public set grid(value: Grid) {
        // Method Implementation: simple
        this._grid = value;
        this._isGridDefined = true;
    }

    private _isGridDefined: boolean;

    public get isGridDefined(): boolean {
        // Method Implementation: simple
        return this._isGridDefined;
    }

    public constructor();
    public constructor(source: IChildGridField);
    public constructor(source: Partial<Pick<ChildGridField, 'grid' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>);
    public constructor(source?: IChildGridField | Partial<Pick<ChildGridField, 'grid' | 'columnName' | 'source' | 'enabled' | 'required' | 'validationState' | 'validationMessages' | 'displayValue' | 'assignmentState' | 'recommendedDisplayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isChildGridFieldModel(source)) {
                if (source.isGridDefined) {
                    this._grid = source.grid;
                    this._isGridDefined = true;
                }
                else {
                    this._grid = new Grid();
                    this._isGridDefined = true;
                }
            }
            else {
                if (source.grid !== undefined) {
                    this._grid = source.grid;
                    this._isGridDefined = true;
                }
                else {
                    this._grid = new Grid();
                    this._isGridDefined = true;
                }
            }
        }
        else {
            super();
        
            this._grid = new Grid();
            this._isGridDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...ChildGridField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isGridDefined && this.grid !== undefined)
            definedProperties.push('grid');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'grid':
                return this.grid;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isGridDefined && this.grid !== undefined) {
            if (this.grid !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('grid', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.grid.getModelName(), this.grid.getDefinedPropertyNames().length));
                this.grid.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('grid', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'grid':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === Grid.modelName) {
                        this.grid = new Grid();
                        this.grid.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Grid.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'grid' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'grid'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // grid
        if (this.isGridDefined && this.grid !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.grid === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.grid.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // grid
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.grid = new Grid();
                    this.grid.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ChildGridField {
        // Method Implementation: simple
        const schema = ChildGridFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ChildGridFieldZodSchemaShape): ChildGridField {
        // Method Implementation: parseZodSchema
        return new ChildGridField({
            columnName: schema.columnName,
            source: schema.source,
            enabled: schema.enabled,
            required: schema.required,
            validationState: (() => {
            if (!schema.validationState)
                return;
        
            switch (schema.validationState) {
                case 'Valid':
                    return FieldValidationStates.Valid;
                case 'SoftInvalid':
                    return FieldValidationStates.SoftInvalid;
                case 'HardInvalid':
                    return FieldValidationStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.validationState" not recognized`);
            }
        })(),
            validationMessages: schema.validationMessages?.map(i => ValidationMessage.parseZodSchema(i)),
            displayValue: schema.displayValue,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Unassigned':
                    return Core.AssignmentStates.Unassigned;
                case 'UserAssigned':
                    return Core.AssignmentStates.UserAssigned;
                case 'SystemAssigned':
                    return Core.AssignmentStates.SystemAssigned;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            recommendedDisplayValue: schema.recommendedDisplayValue,
            grid: schema.grid && Grid.parseZodSchema(schema.grid)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'childGrid',
            ...super.toJSON(),
            grid: this.isGridDefined ? (this.grid) : undefined,
        };
    }

    public clone(): ChildGridField {
        // Method Implementation: clone
        return new ChildGridField({
            columnName: this.isColumnNameDefined ? this.columnName : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validationState: this.isValidationStateDefined ? this.validationState : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            recommendedDisplayValue: this.isRecommendedDisplayValueDefined ? this.recommendedDisplayValue : undefined,
            grid: this.isGridDefined ? this.grid.clone() : undefined
        });
    }
}

export abstract class AAnswer implements IAnswer {
    public static readonly modelName: string = 'Answer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'displayName', 'enabled', 'visible', 'assignmentState' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AAnswer.modelName;
    }

    private _name: string;

    private _displayName: string;

    private _enabled: boolean;

    private _visible: boolean;

    private _assignmentState: Core.OptionStates;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get displayName(): string {
        // Method Implementation: simple
        return this._displayName;
    }

    public get enabled(): boolean {
        // Method Implementation: simple
        return this._enabled;
    }

    public get visible(): boolean {
        // Method Implementation: simple
        return this._visible;
    }

    public get assignmentState(): Core.OptionStates {
        // Method Implementation: simple
        return this._assignmentState;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set displayName(value: string) {
        // Method Implementation: simple
        this._displayName = value;
        this._isDisplayNameDefined = true;
    }

    public set enabled(value: boolean) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set visible(value: boolean) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set assignmentState(value: Core.OptionStates) {
        // Method Implementation: simple
        this._assignmentState = value;
        this._isAssignmentStateDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDisplayNameDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isAssignmentStateDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDisplayNameDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayNameDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isAssignmentStateDefined(): boolean {
        // Method Implementation: simple
        return this._isAssignmentStateDefined;
    }

    public constructor();
    public constructor(source: IAnswer);
    public constructor(source: Partial<Pick<AAnswer, 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>);
    public constructor(source?: IAnswer | Partial<Pick<AAnswer, 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isAnswerModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDisplayNameDefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.isAssignmentStateDefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.OptionStates.Assignable;
                    this._isAssignmentStateDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.displayName !== undefined) {
                    this._displayName = source.displayName;
                    this._isDisplayNameDefined = true;
                }
                else {
                    this._displayName = '';
                    this._isDisplayNameDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = false;
                    this._isEnabledDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = false;
                    this._isVisibleDefined = false;
                }
                
                if (source.assignmentState !== undefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = Core.OptionStates.Assignable;
                    this._isAssignmentStateDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._displayName = '';
            this._isDisplayNameDefined = false;
        
            this._enabled = false;
            this._isEnabledDefined = false;
        
            this._visible = false;
            this._isVisibleDefined = false;
        
            this._assignmentState = Core.OptionStates.Assignable;
            this._isAssignmentStateDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AAnswer.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDisplayNameDefined && this.displayName !== undefined)
            definedProperties.push('displayName');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined)
            definedProperties.push('assignmentState');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'displayName':
                return this.displayName;
            case 'enabled':
                return this.enabled;
            case 'visible':
                return this.visible;
            case 'assignmentState':
                return this.assignmentState;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            if (this.displayName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayName', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            if (this.assignmentState !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.assignmentState);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'displayName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayName' cannot be null`);
                    this.displayName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayName'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.enabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'enabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.visible = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'visible' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'assignmentState':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.assignmentState = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'assignmentState' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'assignmentState'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayName
        if (this.isDisplayNameDefined && this.displayName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.enabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.visible);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // assignmentState
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.assignmentState);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayName = <any>null;
                    break;
                case 1:
                    this.displayName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.enabled = reader.readBooleanValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.visible = reader.readBooleanValue();
        }
        
        // assignmentState
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.assignmentState = reader.readEnumValue();
        }
    }

    public static parse(hash: unknown): AAnswer {
        // Method Implementation: simple
        const schema = AnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: AnswerZodSchemaShape): AAnswer {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.answerType)
        {
            case 'stringAnswer':
            case 'string':
                return StringAnswer.parse(schema);
            case 'numericAnswer':
            case 'numeric':
                return NumericAnswer.parse(schema);
            default:
                throw new Error(`answerType of ${ schema.answerType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            displayName: this.isDisplayNameDefined ? ((this.displayName === null ? undefined : this.displayName)) : undefined,
            enabled: this.isEnabledDefined ? (this.enabled) : undefined,
            visible: this.isVisibleDefined ? (this.visible) : undefined,
            assignmentState: this.isAssignmentStateDefined ? (Core.OptionStates[this.assignmentState]) : undefined,
        };
    }

    public abstract clone(): AAnswer;
}

export class StringAnswer extends AAnswer implements IStringAnswer {
    public static readonly modelName: string = 'StringAnswer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringAnswer.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringAnswer);
    public constructor(source: Partial<Pick<StringAnswer, 'value' | 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>);
    public constructor(source?: IStringAnswer | Partial<Pick<StringAnswer, 'value' | 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringAnswerModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringAnswer.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringAnswer {
        // Method Implementation: simple
        const schema = StringAnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringAnswerZodSchemaShape): StringAnswer {
        // Method Implementation: parseZodSchema
        return new StringAnswer({
            name: schema.name,
            displayName: schema.displayName,
            enabled: schema.enabled,
            visible: schema.visible,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Assignable':
                    return Core.OptionStates.Assignable;
                case 'Conflicting':
                    return Core.OptionStates.Conflicting;
                case 'Blocked':
                    return Core.OptionStates.Blocked;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            answerType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringAnswer {
        // Method Implementation: clone
        return new StringAnswer({
            name: this.isNameDefined ? this.name : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericAnswer extends AAnswer implements INumericAnswer {
    public static readonly modelName: string = 'NumericAnswer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericAnswer.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: INumericAnswer);
    public constructor(source: Partial<Pick<NumericAnswer, 'value' | 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>);
    public constructor(source?: INumericAnswer | Partial<Pick<NumericAnswer, 'value' | 'name' | 'displayName' | 'enabled' | 'visible' | 'assignmentState'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericAnswerModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericAnswer.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericAnswer {
        // Method Implementation: simple
        const schema = NumericAnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericAnswerZodSchemaShape): NumericAnswer {
        // Method Implementation: parseZodSchema
        return new NumericAnswer({
            name: schema.name,
            displayName: schema.displayName,
            enabled: schema.enabled,
            visible: schema.visible,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Assignable':
                    return Core.OptionStates.Assignable;
                case 'Conflicting':
                    return Core.OptionStates.Conflicting;
                case 'Blocked':
                    return Core.OptionStates.Blocked;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })(),
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            answerType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): NumericAnswer {
        // Method Implementation: clone
        return new NumericAnswer({
            name: this.isNameDefined ? this.name : undefined,
            displayName: this.isDisplayNameDefined ? this.displayName : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class ValidationMessage implements IValidationMessage {
    public static readonly modelName: string = 'ValidationMessage';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'message', 'type', 'transient' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ValidationMessage.modelName;
    }

    private _message: string;

    private _type: ValidationMessageStates;

    private _transient: boolean;

    public get message(): string {
        // Method Implementation: simple
        return this._message;
    }

    public get type(): ValidationMessageStates {
        // Method Implementation: simple
        return this._type;
    }

    public get transient(): boolean {
        // Method Implementation: simple
        return this._transient;
    }

    public set message(value: string) {
        // Method Implementation: simple
        this._message = value;
        this._isMessageDefined = true;
    }

    public set type(value: ValidationMessageStates) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    public set transient(value: boolean) {
        // Method Implementation: simple
        this._transient = value;
        this._isTransientDefined = true;
    }

    private _isMessageDefined: boolean;

    private _isTypeDefined: boolean;

    private _isTransientDefined: boolean;

    public get isMessageDefined(): boolean {
        // Method Implementation: simple
        return this._isMessageDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public get isTransientDefined(): boolean {
        // Method Implementation: simple
        return this._isTransientDefined;
    }

    public constructor();
    public constructor(source: IValidationMessage);
    public constructor(source: Partial<Pick<ValidationMessage, 'message' | 'type' | 'transient'>>);
    public constructor(source?: IValidationMessage | Partial<Pick<ValidationMessage, 'message' | 'type' | 'transient'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isValidationMessageModel(source)) {
                if (source.isMessageDefined) {
                    this._message = source.message;
                    this._isMessageDefined = true;
                }
                else {
                    this._message = '';
                    this._isMessageDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = ValidationMessageStates.Info;
                    this._isTypeDefined = false;
                }
                
                if (source.isTransientDefined) {
                    this._transient = source.transient;
                    this._isTransientDefined = true;
                }
                else {
                    this._transient = false;
                    this._isTransientDefined = false;
                }
            }
            else {
                if (source.message !== undefined) {
                    this._message = source.message;
                    this._isMessageDefined = true;
                }
                else {
                    this._message = '';
                    this._isMessageDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = ValidationMessageStates.Info;
                    this._isTypeDefined = false;
                }
                
                if (source.transient !== undefined) {
                    this._transient = source.transient;
                    this._isTransientDefined = true;
                }
                else {
                    this._transient = false;
                    this._isTransientDefined = false;
                }
            }
        }
        else {
            this._message = '';
            this._isMessageDefined = false;
        
            this._type = ValidationMessageStates.Info;
            this._isTypeDefined = false;
        
            this._transient = false;
            this._isTransientDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ValidationMessage.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isMessageDefined && this.message !== undefined)
            definedProperties.push('message');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        if (this.isTransientDefined && this.transient !== undefined)
            definedProperties.push('transient');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'message':
                return this.message;
            case 'type':
                return this.type;
            case 'transient':
                return this.transient;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isMessageDefined && this.message !== undefined) {
            if (this.message !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('message', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.message);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('message', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
        
        if (this.isTransientDefined && this.transient !== undefined) {
            if (this.transient !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('transient', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.transient);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('transient', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'message':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.message = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'message' cannot be null`);
                    this.message = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'message'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.type = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'type' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            case 'transient':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.transient = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'transient' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'transient'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // message
        if (this.isMessageDefined && this.message !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.message === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.message);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.type);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // transient
        if (this.isTransientDefined && this.transient !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.transient);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // message
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.message = <any>null;
                    break;
                case 1:
                    this.message = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.type = reader.readEnumValue();
        }
        
        // transient
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.transient = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): ValidationMessage {
        // Method Implementation: simple
        const schema = ValidationMessageZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ValidationMessageZodSchemaShape): ValidationMessage {
        // Method Implementation: parseZodSchema
        return new ValidationMessage({
            message: schema.message,
            type: (() => {
            if (!schema.type)
                return;
        
            switch (schema.type) {
                case 'Info':
                    return ValidationMessageStates.Info;
                case 'SoftInvalid':
                    return ValidationMessageStates.SoftInvalid;
                case 'HardInvalid':
                    return ValidationMessageStates.HardInvalid;
                default:
                    throw new Error(`Enum type "schema.type" not recognized`);
            }
        })(),
            transient: schema.transient
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            message: this.isMessageDefined ? ((this.message === null ? undefined : this.message)) : undefined,
            type: this.isTypeDefined ? (ValidationMessageStates[this.type]) : undefined,
            transient: this.isTransientDefined ? (this.transient) : undefined,
        };
    }

    public clone(): ValidationMessage {
        // Method Implementation: clone
        return new ValidationMessage({
            message: this.isMessageDefined ? this.message : undefined,
            type: this.isTypeDefined ? this.type : undefined,
            transient: this.isTransientDefined ? this.transient : undefined
        });
    }
}

export class ConflictScenario implements IConflictScenario {
    public static readonly modelName: string = 'ConflictScenario';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'fields' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConflictScenario.modelName;
    }

    private _fields: ConflictScenarioChangedField[];

    public get fields(): ConflictScenarioChangedField[] {
        // Method Implementation: simple
        return this._fields;
    }

    public set fields(value: ConflictScenarioChangedField[]) {
        // Method Implementation: simple
        this._fields = value;
        this._isFieldsDefined = true;
    }

    private _isFieldsDefined: boolean;

    public get isFieldsDefined(): boolean {
        // Method Implementation: simple
        return this._isFieldsDefined;
    }

    public constructor();
    public constructor(source: IConflictScenario);
    public constructor(source: Partial<Pick<ConflictScenario, 'fields'>>);
    public constructor(source?: IConflictScenario | Partial<Pick<ConflictScenario, 'fields'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConflictScenarioModel(source)) {
                if (source.isFieldsDefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
            }
            else {
                if (source.fields !== undefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
            }
        }
        else {
            this._fields = [];
            this._isFieldsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConflictScenario.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isFieldsDefined && this.fields !== undefined)
            definedProperties.push('fields');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'fields':
                return this.fields;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isFieldsDefined && this.fields !== undefined) {
            if (this.fields !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.fields.length));
                for (let i = 0; i < this.fields.length; i++) {
                    const item = this.fields[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'fields':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.fields = [];
                
                    let item: ConflictScenarioChangedField = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ConflictScenarioChangedField.modelName) {
                                item = new ConflictScenarioChangedField();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConflictScenarioChangedField.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'fields' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'fields'`);
                        }
                
                        this.fields.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'fields' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'fields'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // fields
        if (this.isFieldsDefined && this.fields !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.fields === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.fields.length);
            
                for (const item of this.fields) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // fields
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ConflictScenarioChangedField;
            
                    this.fields = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ConflictScenarioChangedField();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.fields.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConflictScenario {
        // Method Implementation: simple
        const schema = ConflictScenarioZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConflictScenarioZodSchemaShape): ConflictScenario {
        // Method Implementation: parseZodSchema
        return new ConflictScenario({
            fields: schema.fields?.map(i => ConflictScenarioChangedField.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fields: this.isFieldsDefined ? (this.fields?.map(item => item)) : undefined,
        };
    }

    public clone(): ConflictScenario {
        // Method Implementation: clone
        return new ConflictScenario({
            fields: this.isFieldsDefined ? this.fields?.map(i => i.clone()) : undefined
        });
    }
}

export class ConflictScenarioChangedField implements IConflictScenarioChangedField {
    public static readonly modelName: string = 'ConflictScenarioChangedField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'source', 'columnName', 'oldValue', 'newValue' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConflictScenarioChangedField.modelName;
    }

    private _source: string;

    private _columnName: string;

    private _oldValue: string;

    private _newValue: string;

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get columnName(): string {
        // Method Implementation: simple
        return this._columnName;
    }

    public get oldValue(): string {
        // Method Implementation: simple
        return this._oldValue;
    }

    public get newValue(): string {
        // Method Implementation: simple
        return this._newValue;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set columnName(value: string) {
        // Method Implementation: simple
        this._columnName = value;
        this._isColumnNameDefined = true;
    }

    public set oldValue(value: string) {
        // Method Implementation: simple
        this._oldValue = value;
        this._isOldValueDefined = true;
    }

    public set newValue(value: string) {
        // Method Implementation: simple
        this._newValue = value;
        this._isNewValueDefined = true;
    }

    private _isSourceDefined: boolean;

    private _isColumnNameDefined: boolean;

    private _isOldValueDefined: boolean;

    private _isNewValueDefined: boolean;

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isColumnNameDefined(): boolean {
        // Method Implementation: simple
        return this._isColumnNameDefined;
    }

    public get isOldValueDefined(): boolean {
        // Method Implementation: simple
        return this._isOldValueDefined;
    }

    public get isNewValueDefined(): boolean {
        // Method Implementation: simple
        return this._isNewValueDefined;
    }

    public constructor();
    public constructor(source: IConflictScenarioChangedField);
    public constructor(source: Partial<Pick<ConflictScenarioChangedField, 'source' | 'columnName' | 'oldValue' | 'newValue'>>);
    public constructor(source?: IConflictScenarioChangedField | Partial<Pick<ConflictScenarioChangedField, 'source' | 'columnName' | 'oldValue' | 'newValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConflictScenarioChangedFieldModel(source)) {
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isColumnNameDefined) {
                    this._columnName = source.columnName;
                    this._isColumnNameDefined = true;
                }
                else {
                    this._columnName = '';
                    this._isColumnNameDefined = false;
                }
                
                if (source.isOldValueDefined) {
                    this._oldValue = source.oldValue;
                    this._isOldValueDefined = true;
                }
                else {
                    this._oldValue = '';
                    this._isOldValueDefined = false;
                }
                
                if (source.isNewValueDefined) {
                    this._newValue = source.newValue;
                    this._isNewValueDefined = true;
                }
                else {
                    this._newValue = '';
                    this._isNewValueDefined = false;
                }
            }
            else {
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.columnName !== undefined) {
                    this._columnName = source.columnName;
                    this._isColumnNameDefined = true;
                }
                else {
                    this._columnName = '';
                    this._isColumnNameDefined = false;
                }
                
                if (source.oldValue !== undefined) {
                    this._oldValue = source.oldValue;
                    this._isOldValueDefined = true;
                }
                else {
                    this._oldValue = '';
                    this._isOldValueDefined = false;
                }
                
                if (source.newValue !== undefined) {
                    this._newValue = source.newValue;
                    this._isNewValueDefined = true;
                }
                else {
                    this._newValue = '';
                    this._isNewValueDefined = false;
                }
            }
        }
        else {
            this._source = '';
            this._isSourceDefined = false;
        
            this._columnName = '';
            this._isColumnNameDefined = false;
        
            this._oldValue = '';
            this._isOldValueDefined = false;
        
            this._newValue = '';
            this._isNewValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConflictScenarioChangedField.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isColumnNameDefined && this.columnName !== undefined)
            definedProperties.push('columnName');
        if (this.isOldValueDefined && this.oldValue !== undefined)
            definedProperties.push('oldValue');
        if (this.isNewValueDefined && this.newValue !== undefined)
            definedProperties.push('newValue');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'source':
                return this.source;
            case 'columnName':
                return this.columnName;
            case 'oldValue':
                return this.oldValue;
            case 'newValue':
                return this.newValue;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isColumnNameDefined && this.columnName !== undefined) {
            if (this.columnName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('columnName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.columnName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('columnName', 'null'));
            }
        }
        
        if (this.isOldValueDefined && this.oldValue !== undefined) {
            if (this.oldValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('oldValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.oldValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('oldValue', 'null'));
            }
        }
        
        if (this.isNewValueDefined && this.newValue !== undefined) {
            if (this.newValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('newValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.newValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('newValue', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'columnName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.columnName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'columnName' cannot be null`);
                    this.columnName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'columnName'`);
                }
        
                return true;
            case 'oldValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.oldValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'oldValue' cannot be null`);
                    this.oldValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'oldValue'`);
                }
        
                return true;
            case 'newValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.newValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'newValue' cannot be null`);
                    this.newValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'newValue'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // columnName
        if (this.isColumnNameDefined && this.columnName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.columnName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.columnName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // oldValue
        if (this.isOldValueDefined && this.oldValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.oldValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.oldValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // newValue
        if (this.isNewValueDefined && this.newValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.newValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.newValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // columnName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.columnName = <any>null;
                    break;
                case 1:
                    this.columnName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // oldValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.oldValue = <any>null;
                    break;
                case 1:
                    this.oldValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // newValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.newValue = <any>null;
                    break;
                case 1:
                    this.newValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConflictScenarioChangedField {
        // Method Implementation: simple
        const schema = ConflictScenarioChangedFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConflictScenarioChangedFieldZodSchemaShape): ConflictScenarioChangedField {
        // Method Implementation: parseZodSchema
        return new ConflictScenarioChangedField({
            source: schema.source,
            columnName: schema.columnName,
            oldValue: schema.oldValue,
            newValue: schema.newValue
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            columnName: this.isColumnNameDefined ? ((this.columnName === null ? undefined : this.columnName)) : undefined,
            oldValue: this.isOldValueDefined ? ((this.oldValue === null ? undefined : this.oldValue)) : undefined,
            newValue: this.isNewValueDefined ? ((this.newValue === null ? undefined : this.newValue)) : undefined,
        };
    }

    public clone(): ConflictScenarioChangedField {
        // Method Implementation: clone
        return new ConflictScenarioChangedField({
            source: this.isSourceDefined ? this.source : undefined,
            columnName: this.isColumnNameDefined ? this.columnName : undefined,
            oldValue: this.isOldValueDefined ? this.oldValue : undefined,
            newValue: this.isNewValueDefined ? this.newValue : undefined
        });
    }
}

export class RecommendedValue implements IRecommendedValue {
    public static readonly modelName: string = 'RecommendedValue';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'source', 'displayValue' ];

    public get modelName(): string {
        // Method Implementation: simple
        return RecommendedValue.modelName;
    }

    private _source: string;

    private _displayValue: string;

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get displayValue(): string {
        // Method Implementation: simple
        return this._displayValue;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set displayValue(value: string) {
        // Method Implementation: simple
        this._displayValue = value;
        this._isDisplayValueDefined = true;
    }

    private _isSourceDefined: boolean;

    private _isDisplayValueDefined: boolean;

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isDisplayValueDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayValueDefined;
    }

    public constructor();
    public constructor(source: IRecommendedValue);
    public constructor(source: Partial<Pick<RecommendedValue, 'source' | 'displayValue'>>);
    public constructor(source?: IRecommendedValue | Partial<Pick<RecommendedValue, 'source' | 'displayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isRecommendedValueModel(source)) {
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isDisplayValueDefined) {
                    this._displayValue = source.displayValue;
                    this._isDisplayValueDefined = true;
                }
                else {
                    this._displayValue = '';
                    this._isDisplayValueDefined = false;
                }
            }
            else {
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.displayValue !== undefined) {
                    this._displayValue = source.displayValue;
                    this._isDisplayValueDefined = true;
                }
                else {
                    this._displayValue = '';
                    this._isDisplayValueDefined = false;
                }
            }
        }
        else {
            this._source = '';
            this._isSourceDefined = false;
        
            this._displayValue = '';
            this._isDisplayValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return RecommendedValue.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isDisplayValueDefined && this.displayValue !== undefined)
            definedProperties.push('displayValue');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'source':
                return this.source;
            case 'displayValue':
                return this.displayValue;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isDisplayValueDefined && this.displayValue !== undefined) {
            if (this.displayValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayValue', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'displayValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayValue' cannot be null`);
                    this.displayValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayValue'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayValue
        if (this.isDisplayValueDefined && this.displayValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayValue = <any>null;
                    break;
                case 1:
                    this.displayValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): RecommendedValue {
        // Method Implementation: simple
        const schema = RecommendedValueZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: RecommendedValueZodSchemaShape): RecommendedValue {
        // Method Implementation: parseZodSchema
        return new RecommendedValue({
            source: schema.source,
            displayValue: schema.displayValue
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            displayValue: this.isDisplayValueDefined ? ((this.displayValue === null ? undefined : this.displayValue)) : undefined,
        };
    }

    public clone(): RecommendedValue {
        // Method Implementation: clone
        return new RecommendedValue({
            source: this.isSourceDefined ? this.source : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Grid':
                return new Grid();
            case 'HistoricalState':
                return new HistoricalState();
            case 'Column':
                return new Column();
            case 'ColumnAnswer':
                return new ColumnAnswer();
            case 'Row':
                return new Row();
            case 'StringField':
                return new StringField();
            case 'NumericField':
                return new NumericField();
            case 'BooleanField':
                return new BooleanField();
            case 'StringEnumerableField':
                return new StringEnumerableField();
            case 'NumericEnumerableField':
                return new NumericEnumerableField();
            case 'MultiStringField':
                return new MultiStringField();
            case 'MultiNumericField':
                return new MultiNumericField();
            case 'ChildGridField':
                return new ChildGridField();
            case 'StringAnswer':
                return new StringAnswer();
            case 'NumericAnswer':
                return new NumericAnswer();
            case 'ValidationMessage':
                return new ValidationMessage();
            case 'ConflictScenario':
                return new ConflictScenario();
            case 'ConflictScenarioChangedField':
                return new ConflictScenarioChangedField();
            case 'RecommendedValue':
                return new RecommendedValue();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Grid':
                return new Grid(source);
            case 'HistoricalState':
                return new HistoricalState(source);
            case 'Column':
                return new Column(source);
            case 'ColumnAnswer':
                return new ColumnAnswer(source);
            case 'Row':
                return new Row(source);
            case 'StringField':
                return new StringField(source);
            case 'NumericField':
                return new NumericField(source);
            case 'BooleanField':
                return new BooleanField(source);
            case 'StringEnumerableField':
                return new StringEnumerableField(source);
            case 'NumericEnumerableField':
                return new NumericEnumerableField(source);
            case 'MultiStringField':
                return new MultiStringField(source);
            case 'MultiNumericField':
                return new MultiNumericField(source);
            case 'ChildGridField':
                return new ChildGridField(source);
            case 'StringAnswer':
                return new StringAnswer(source);
            case 'NumericAnswer':
                return new NumericAnswer(source);
            case 'ValidationMessage':
                return new ValidationMessage(source);
            case 'ConflictScenario':
                return new ConflictScenario(source);
            case 'ConflictScenarioChangedField':
                return new ConflictScenarioChangedField(source);
            case 'RecommendedValue':
                return new RecommendedValue(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Grid':
                return Grid.parse(source);
            case 'HistoricalState':
                return HistoricalState.parse(source);
            case 'Column':
                return Column.parse(source);
            case 'ColumnAnswer':
                return ColumnAnswer.parse(source);
            case 'Row':
                return Row.parse(source);
            case 'StringField':
                return StringField.parse(source);
            case 'NumericField':
                return NumericField.parse(source);
            case 'BooleanField':
                return BooleanField.parse(source);
            case 'StringEnumerableField':
                return StringEnumerableField.parse(source);
            case 'NumericEnumerableField':
                return NumericEnumerableField.parse(source);
            case 'MultiStringField':
                return MultiStringField.parse(source);
            case 'MultiNumericField':
                return MultiNumericField.parse(source);
            case 'ChildGridField':
                return ChildGridField.parse(source);
            case 'StringAnswer':
                return StringAnswer.parse(source);
            case 'NumericAnswer':
                return NumericAnswer.parse(source);
            case 'ValidationMessage':
                return ValidationMessage.parse(source);
            case 'ConflictScenario':
                return ConflictScenario.parse(source);
            case 'ConflictScenarioChangedField':
                return ConflictScenarioChangedField.parse(source);
            case 'RecommendedValue':
                return RecommendedValue.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
